import { filterSessionsByCourse } from '../../utils/sessionUtils';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus, ArrowLeft, Upload, DollarSign, CheckCircle, Eye, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import api from '@/api/api';
import { INDIAN_STATES, INDIAN_STATES_DISTRICTS } from '@/utils/indianStatesDistricts';
import { generateAdmissionReceipt } from '@/utils/admissionReceiptGenerator';
import PaymentBreakdown from '@/components/PaymentBreakdown';
import MethodSubjectField from '@/components/MethodSubjectField';

const StudentAdmission = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('booking');
  const editStudentId = searchParams.get('edit');
  const onlineApplicationId = searchParams.get('online_application');
  
  console.log('=== REGULAR ENTRY ADMISSION FORM ===');
  console.log('Booking ID from URL:', bookingId);
  console.log('Edit Student ID from URL:', editStudentId);
  console.log('Online Application ID from URL:', onlineApplicationId);
  console.log('Full URL:', window.location.href);
  const [loading, setLoading] = useState(false);
  const [onlineApplicationData, setOnlineApplicationData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [semesters, setSemesters] = useState([]);
  const [sections, setSections] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const [consultantPayout, setConsultantPayout] = useState(0);
  const [bookingData, setBookingData] = useState(null);
  const [discounts, setDiscounts] = useState([]);
  const [scholarships, setScholarships] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [appliedDiscounts, setAppliedDiscounts] = useState([]);
  const [appliedScholarships, setAppliedScholarships] = useState([]);
  const [courseFeeManuallyEdited, setCourseFeeManuallyEdited] = useState(false);
  // Tracks manual edits to the Admission Fee field — once user types a value,
  // subsequent auto-fetches (course/session/lateral toggles) must NOT overwrite it.
  const [admissionAmountManuallyEdited, setAdmissionAmountManuallyEdited] = useState(false);
  const [collegeInfo, setCollegeInfo] = useState({ college_code: '' });
  const [languages, setLanguages] = useState([]);
  const [religions, setReligions] = useState([]);
  const [castes, setCastes] = useState([]);
  const [documentSettings, setDocumentSettings] = useState([]);
  // Set loading true initially if booking mode (Convert from Admission Hub)
  const [documentSettingsLoading, setDocumentSettingsLoading] = useState(!!bookingId);
  const [selectedCourseData, setSelectedCourseData] = useState(null); // Full course object with lateral_entry_allowed
  const [isApproved, setIsApproved] = useState(false); // Track if student is approved (read-only mode)
  
  // Success Modal State
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successData, setSuccessData] = useState({
    studentName: '',
    admissionNumber: '',
    courseName: '',
    studentId: ''
  });
  
  const [documents, setDocuments] = useState({
    aadhar_card: null,
    class_10_registration: null,
    class_10_admit: null,
    class_10_result: null,
    class_12_result: null,
    diploma_certificate: null,
    graduation_certificate: null,
    masters_certificate: null,
    phd_certificate: null,
    other_certificates: null,
    profile_photo: null,
    signature: null
  });
  const [masterDataLoaded, setMasterDataLoaded] = useState(false);
  const [formData, setFormData] = useState({
    admission_no: '',
    roll_number: '',
    university_id: '',
    method_subject: '',
    is_lateral_entry: false, // Entry type toggle
    entry_semester: 3, // Default for lateral entry
    
    // Student Basic Info
    first_name: '',
    middle_name: '',
    last_name: '',
    email: '',
    phone: '',
    whatsapp_no: '',
    date_of_birth: '',
    gender: 'Male',
    religion: '',
    caste_category: '',
    blood_group: '',
    aadhar_no: '',
    nationality: 'Indian',
    medium_of_instruction: '',
    
    // Academic Details (Regular Entry)
    class_10_board: '',
    class_10_school: '',
    class_10_year: '',
    class_10_percentage: '',
    class_12_board: '',
    class_12_school: '',
    class_12_year: '',
    class_12_percentage: '',
    
    // Lateral Entry - Diploma Details
    diploma_name: '',
    diploma_college: '',
    diploma_board: '',
    diploma_year: '',
    diploma_percentage: '',
    gap_year: '',
    gap_reason: '',
    
    // Optional Higher Education
    has_graduation: false,
    graduation_degree: '',
    graduation_college: '',
    graduation_university: '',
    graduation_year: '',
    graduation_percentage: '',
    
    has_masters: false,
    masters_degree: '',
    masters_college: '',
    masters_university: '',
    masters_year: '',
    masters_percentage: '',
    
    has_phd: false,
    phd_title: '',
    phd_university: '',
    phd_year: '',
    phd_status: '',
    
    // Address
    address: '',
    city: '',
    state: '',
    district: '',
    pin_code: '',
    landmark: '',
    
    // Permanent Address
    permanent_address: '',
    permanent_city: '',
    permanent_state: '',
    permanent_district: '',
    permanent_pin_code: '',
    permanent_landmark: '',
    
    // Guardian Info
    father_name: '',
    father_phone: '',
    father_email: '',
    father_occupation: '',
    mother_name: '',
    mother_phone: '',
    mother_email: '',
    mother_occupation: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_relation: 'Father',
    
    // Course Info
    course_id: '',
    semester_id: '',
    section_id: '',
    admission_date: new Date().toISOString().split('T')[0],
    session: '',
    is_lateral: false,
    
    // Consultant & Fee
    consultant_id: '',
    seat_booking_amount: 0,  // From seat booking if converted
    admission_amount: 0,      // From admission fee settings
    admission_payment_mode: '',  // Legacy field
    admission_payment_modes: [],  // Array of {mode, amount}
    total_course_fee: 0,      // From fee structure
    discount_id: '',          // Temp selected discount
    discount_amount: 0,       // Total discount value
    scholarship_id: '',       // Temp selected scholarship
    scholarship_amount: 0,    // Total scholarship value
    applied_discounts: [],    // Array of applied discount IDs
    applied_scholarships: [], // Array of applied scholarship IDs
    balance_fee: 0,           // Calculated balance
    admission_receipt_no: '', // Auto-generated receipt number
    admission_fee: 0          // Keep for backward compatibility
  });

  const [sameAsPermanent, setSameAsPermanent] = useState(false);

  const handleEntryTypeChange = (isLateral) => {
    console.log('🔄 Entry type changed to:', isLateral ? 'Lateral' : 'Regular Entry');
    
    // Get lateral entry semester from course settings (default: 3)
    const lateralSemester = selectedCourseData?.lateral_entry_semester || 3;
    
    // Find appropriate semester based on entry type
    let targetSemester = null;
    if (isLateral) {
      // Find the lateral entry semester (e.g., 3rd semester)
      targetSemester = semesters.find(s => 
        s.name === `Semester ${lateralSemester}` || 
        s.name === String(lateralSemester) || 
        s.semester_number === lateralSemester
      );
      console.log(`🔍 Looking for Semester ${lateralSemester} for lateral entry:`, targetSemester);
    } else {
      // Find 1st semester for regular entry
      targetSemester = semesters.find(s => s.name === 'Semester 1' || s.name === '1' || s.semester_number === 1);
      console.log('🔍 Looking for 1st semester:', targetSemester);
    }
    
    setFormData(prev => {
      console.log('📚 Auto-selecting semester:', targetSemester?.name || 'Not found');
      
      // Re-fetch fees and semesters based on new entry type AFTER state update
      setTimeout(() => {
        if (prev.course_id) {
          console.log(`📚 Re-fetching semesters for ${isLateral ? 'LATERAL' : 'REGULAR'} entry`);
          fetchSemesters(prev.course_id, isLateral);
        }
        
        if (prev.course_id && prev.session) {
          console.log(`💰 Re-fetching fees for ${isLateral ? 'LATERAL' : 'REGULAR'} entry`);
          const sessionObj = sessions.find(s => s.name === prev.session);
          if (sessionObj) {
            fetchAdmissionFee(prev.course_id, sessionObj.id, isLateral);
          }
          fetchCourseFee(prev.course_id, prev.session, isLateral);
        }
      }, 100);
      
      // Validate that the previously selected session is still visible after toggle.
      // If institute has Lateral-specific sessions but user was on a Regular one (and vice versa),
      // clear the session to avoid confusing "stale value not in dropdown" state.
      let nextSession = prev.session;
      if (prev.course_id && prev.session) {
        const courseSessions = filterSessionsByCourse(sessions, prev.course_id);
        const hasLateralSpecific = courseSessions.some(s => s.entry_type === 'Lateral');
        const currentSessionObj = courseSessions.find(s => s.name === prev.session);
        const stillVisible = currentSessionObj && (
          isLateral
            ? (hasLateralSpecific ? currentSessionObj.entry_type === 'Lateral' : true)
            : currentSessionObj.entry_type !== 'Lateral'
        );
        if (!stillVisible) {
          console.log(`⚠️ Session "${prev.session}" not available for ${isLateral ? 'lateral' : 'regular'} mode — clearing.`);
          nextSession = '';
        }
      }
      
      return {
        ...prev,
        is_lateral_entry: isLateral,
        entry_semester: isLateral ? lateralSemester : 1,
        admission_no: prev.admission_no && !prev.admission_no.startsWith('ADM') && !prev.admission_no.startsWith('LAT') ? prev.admission_no : '',
        semester_id: targetSemester ? targetSemester.id : '',
        session: nextSession
      };
    });
  };

  // Initial load: Fetch master data first
  useEffect(() => {
    const loadMasterData = async () => {
      console.log('🔄 Loading master data...');
      await fetchMasterData();
      setMasterDataLoaded(true);
      console.log('✅ Master data loaded successfully');
    };
    
    loadMasterData();
  }, []);

  // Once master data is loaded AND sessions are populated, handle student/booking data
  useEffect(() => {
    if (!masterDataLoaded) {
      console.log('⏳ Waiting for master data to load...');
      return;
    }
    
    // Additional check: For edit mode, we need sessions to be populated
    if (editStudentId && sessions.length === 0) {
      console.log('⏳ Waiting for sessions to be populated for edit mode...');
      return;
    }

    const generateAdmissionNumber = () => {
      // For new admissions, admission_no will be auto-generated by backend
      // Only generate receipt number
      const receiptNo = generateAdmissionReceiptNumber();
      setFormData(prev => ({ 
        ...prev, 
        admission_no: '', // Empty - backend will auto-generate
        admission_receipt_no: receiptNo
      }));
      console.log('Generated receipt number:', receiptNo);
    };

    const loadData = async () => {
      if (editStudentId) {
        console.log('📝 Edit mode detected, loading student data...');
        console.log('✅ Sessions available:', sessions.length);
        await fetchStudentData();
      } else if (bookingId) {
        console.log('🎫 Booking conversion detected, loading booking data...');
        console.log('✅ Sessions available:', sessions.length);
        await fetchBookingData();
        // Generate admission number for booking conversion
        console.log('🎫 Generating admission number for booking conversion...');
        generateAdmissionNumber();
      } else if (onlineApplicationId) {
        console.log('🌐 Online application approval detected, loading application data...');
        console.log('✅ Sessions available:', sessions.length);
        await fetchOnlineApplicationData();
        generateAdmissionNumber();
      } else {
        console.log('➕ New admission mode, generating admission number...');
        generateAdmissionNumber();
      }
    };
    
    loadData();
  }, [masterDataLoaded, sessions, bookingId, editStudentId, onlineApplicationId]);

  // Auto-fetch admission fee when sessions load and booking data exists
  useEffect(() => {
    if (sessions.length > 0 && bookingData && formData.course_id && formData.session) {
      console.log('🔄 Sessions loaded, re-attempting admission fee fetch for booking...');
      const sessionObj = sessions.find(s => s.name === formData.session || s.id === formData.session || s.name?.startsWith(formData.session));
      if (sessionObj) {
        console.log('✅ Found session:', sessionObj.name, 'ID:', sessionObj.id);
        fetchAdmissionFee(formData.course_id, sessionObj.id, formData.is_lateral_entry);
      } else {
        console.warn('⚠️ Session not found:', formData.session);
        console.warn('Available sessions:', sessions.map(s => s.name));
      }
    }
  }, [sessions, bookingData]);

  // Auto-select 1st semester for regular entry when semesters are loaded (only for NEW admissions)
  useEffect(() => {
    if (semesters.length > 0 && !formData.semester_id && !bookingId && !editStudentId) {
      const firstSemester = semesters.find(s => s.semester_number === 1);
      if (firstSemester) {
        setFormData(prev => ({ ...prev, semester_id: firstSemester.id }));
        console.log('✅ Auto-selected 1st semester for new regular entry:', firstSemester.id);
      }
    }
  }, [semesters, bookingId, editStudentId]);

  // Auto-fetch consultant payout (FIXED AMOUNT - not percentage based)
  useEffect(() => {
    console.log(`💰 Consultant payout useEffect triggered:`, {
      consultant_id: formData.consultant_id,
      course_id: formData.course_id,
      is_lateral_entry: formData.is_lateral_entry,
      consultants_length: consultants.length
    });
    
    if (formData.consultant_id && formData.course_id && consultants.length > 0) {
      const consultant = consultants.find(c => c.id === formData.consultant_id);
      setSelectedConsultant(consultant);
      
      console.log(`🔍 Found consultant:`, consultant?.name || 'Not found');
      
      if (consultant) {
        // Find payout setting for this course
        const payoutSetting = consultant.course_payout_settings?.find(
          setting => setting.course_id === formData.course_id
        );
        
        // Get fixed payout based on entry type (default 0 if not set)
        const payout = formData.is_lateral_entry 
          ? (payoutSetting?.lateral_payout || 0)
          : (payoutSetting?.regular_payout || 0);
        
        setConsultantPayout(payout);
        console.log(`✓ Consultant payout fetched: ₹${payout} (${formData.is_lateral_entry ? 'Lateral' : 'Regular'} Entry - Fixed Amount)`);
        
        if (payout === 0) {
          console.warn(`⚠️ No payout configured for consultant ${consultant.name} on this course`);
        }
      }
    } else {
      console.log(`⏸️ Not fetching consultant payout - missing requirements`);
      setConsultantPayout(0);
    }
  }, [formData.consultant_id, formData.course_id, formData.is_lateral_entry, consultants]);

  // Auto-calculate balance fee whenever relevant values change
  useEffect(() => {
    const courseFee = formData.total_course_fee || 0;
    const bookingFee = formData.seat_booking_amount || 0;
    const admissionFee = formData.admission_amount || 0;
    const discount = formData.discount_amount || 0;
    const scholarship = formData.scholarship_amount || 0;
    
    // Balance = Course Fee - (Booking + Admission + Discount + Scholarship)
    const balance = courseFee - (bookingFee + admissionFee + discount + scholarship);
    const newBalance = Math.max(0, balance);
    
    if (newBalance !== formData.balance_fee) {
      setFormData(prev => ({ ...prev, balance_fee: newBalance }));
      console.log(`✓ Balance fee auto-calculated: ₹${courseFee} - (₹${bookingFee} + ₹${admissionFee} + ₹${discount} + ₹${scholarship}) = ₹${newBalance}`);
    }
  }, [formData.total_course_fee, formData.seat_booking_amount, formData.admission_amount, formData.discount_amount, formData.scholarship_amount]);

  const fetchStudentData = async () => {
    try {
      console.log('📝 Fetching student data for edit:', editStudentId);
      const response = await api.get(`/students/${editStudentId}`);
      const student = response.data;
      
      console.log('📊 Student data fetched:', student);
      
      // Check if student is approved (has admission confirmed)
      // A student is considered "approved" if they have admission_date and status is not 'Pending'
      const approved = !!(student.admission_date && student.status !== 'Pending');
      setIsApproved(approved);
      console.log('📋 Student approved status:', approved);
      
      // Split full_name into first, middle, last
      const nameParts = (student.full_name || '').trim().split(' ');
      let first_name = '', middle_name = '', last_name = '';
      
      if (nameParts.length === 1) {
        first_name = nameParts[0];
      } else if (nameParts.length === 2) {
        first_name = nameParts[0];
        last_name = nameParts[1];
      } else if (nameParts.length >= 3) {
        first_name = nameParts[0];
        last_name = nameParts[nameParts.length - 1];
        middle_name = nameParts.slice(1, -1).join(' ');
      }
      
      // Fetch semesters for the course to populate dropdown
      if (student.course_id) {
        try {
          const semestersRes = await api.get('/master-settings/semesters', {
            params: { course_id: student.course_id }
          });
          setSemesters(semestersRes.data);
          console.log('📚 Semesters loaded for course:', semestersRes.data.length);
        } catch (err) {
          console.error('Error fetching semesters:', err);
        }
      }
      
      // Wait for sessions to be populated before matching
      console.log('🔍 Checking sessions array:', {
        sessionsLength: sessions.length,
        sessionsAvailable: sessions.map(s => ({ id: s.id, name: s.name }))
      });
      
      // Find session object from the sessions array
      const sessionObj = sessions.find(s => s.name === student.session || s.id === student.session);
      const sessionValue = sessionObj ? sessionObj.name : student.session;
      
      console.log('🔍 Session lookup result:', {
        studentSession: student.session,
        foundSessionObj: sessionObj,
        finalSessionValue: sessionValue
      });
      
      // Pre-fill form with student data - use the session NAME for consistency
      setFormData(prev => ({
        ...prev,
        admission_no: student.admission_number || '',
        roll_number: student.roll_number || '',
        university_id: student.university_id || '',
        method_subject: student.method_subject || '',
        first_name: first_name,
        middle_name: middle_name,
        last_name: last_name,
        email: student.email || '',
        phone: student.phone || '',
        whatsapp_no: student.whatsapp_no || '',
        date_of_birth: student.date_of_birth || '',
        gender: student.gender || '',
        nationality: student.nationality || 'Indian',
        aadhar_no: student.aadhar_no || '',
        // Address Information
        address: student.address || '',
        city: student.city || '',
        state: student.state || '',
        district: student.district || '',
        pin_code: student.pin_code || '',
        landmark: student.landmark || '',
        // Permanent Address Information
        permanent_address: student.permanent_address || '',
        permanent_city: student.permanent_city || '',
        permanent_state: student.permanent_state || '',
        permanent_district: student.permanent_district || '',
        permanent_pin_code: student.permanent_pin_code || '',
        permanent_landmark: student.permanent_landmark || '',
        // Academic Details
        class_10_board: student.class_10_board || '',
        class_10_year: student.class_10_year || '',
        class_10_percentage: student.class_10_percentage || '',
        class_12_board: student.class_12_board || '',
        class_12_year: student.class_12_year || '',
        class_12_percentage: student.class_12_percentage || '',
        // Course Information
        course_id: student.course_id || '',
        semester_id: student.semester_id || '',
        section_id: student.section_id || '',
        session: sessionValue || student.session || '',
        admission_date: student.admission_date || '',
        // Fee Information
        seat_booking_amount: student.seat_booking_amount || 0,
        total_course_fee: student.total_course_fee || 0,
        admission_amount: student.admission_fee || 0,
        admission_payment_mode: student.admission_payment_mode || '',
        admission_payment_modes: student.admission_payment_modes || [],
        discount_amount: student.discount_amount || 0,
        discount_id: student.discount_id || '',
        scholarship_amount: student.scholarship_amount || 0,
        scholarship_id: student.scholarship_id || '',
        balance_fee: student.balance_fee || 0,
        // Father's Information
        father_name: student.father_name || '',
        father_phone: student.father_phone || '',
        father_email: student.father_email || '',
        father_occupation: student.father_occupation || '',
        // Mother's Information
        mother_name: student.mother_name || '',
        mother_phone: student.mother_phone || '',
        mother_email: student.mother_email || '',
        mother_occupation: student.mother_occupation || '',
        // Guardian Information
        guardian_name: student.guardian_name || '',
        guardian_phone: student.guardian_phone || '',
        guardian_relation: student.guardian_relation || '',
        // Other Details
        blood_group: student.blood_group || '',
        religion: student.religion || '',
        caste_category: student.caste || '',
        consultant_id: student.consultant_id || ''
      }));
      
      // Populate applied discounts if discount_id exists
      if (student.discount_id && discounts.length > 0) {
        const existingDiscount = discounts.find(d => d.id === student.discount_id);
        if (existingDiscount) {
          setAppliedDiscounts([existingDiscount]);
          console.log('✅ Loaded existing discount:', existingDiscount.name, '₹', student.discount_amount);
        }
      } else if (student.discount_amount && student.discount_amount > 0) {
        // If discount_id doesn't exist but amount does (old data), create a placeholder
        setAppliedDiscounts([{
          id: 'legacy-discount',
          name: 'Previously Applied Discount',
          type: 'fixed',
          value: student.discount_amount,
          amount: student.discount_amount
        }]);
        console.log('✅ Loaded legacy discount amount:', student.discount_amount);
      }
      
      // Populate applied scholarships if scholarship_id exists
      if (student.scholarship_id && scholarships.length > 0) {
        const existingScholarship = scholarships.find(s => s.id === student.scholarship_id);
        if (existingScholarship) {
          setAppliedScholarships([existingScholarship]);
          console.log('✅ Loaded existing scholarship:', existingScholarship.name, '₹', student.scholarship_amount);
        }
      } else if (student.scholarship_amount && student.scholarship_amount > 0) {
        // If scholarship_id doesn't exist but amount does (old data), create a placeholder
        setAppliedScholarships([{
          id: 'legacy-scholarship',
          name: 'Previously Applied Scholarship',
          type: 'fixed',
          value: student.scholarship_amount,
          amount: student.scholarship_amount
        }]);
        console.log('✅ Loaded legacy scholarship amount:', student.scholarship_amount);
      }
      
      // Load existing documents into documents state so they aren't lost on edit
      if (student.documents && typeof student.documents === 'object') {
        const existingDocs = {};
        for (const [key, value] of Object.entries(student.documents)) {
          if (value) {
            existingDocs[key] = value;
          }
        }
        if (Object.keys(existingDocs).length > 0) {
          setDocuments(prev => ({ ...prev, ...existingDocs }));
          console.log('✅ Loaded existing documents:', Object.keys(existingDocs));
        }
      }
      
      console.log('✅ Student data loaded for editing');
      console.log('✅ Name split:', { first_name, middle_name, last_name });
      console.log('✅ Form populated with session:', sessionValue, 'semester_id:', student.semester_id);
      
      // Fetch fees after student data is loaded
      // CRITICAL FIX: Always fetch course fee by course_id, even if session is null
      if (student.course_id) {
        console.log('💰 Fetching fees for edit mode...');
        console.log('   - Course ID:', student.course_id);
        console.log('   - Session:', student.session || 'NULL');
        
        // In edit mode, preserve the stored course fee (don't overwrite if user had customized)
        if ((student.total_course_fee || 0) > 0) {
          setCourseFeeManuallyEdited(true);
        }
        // In edit mode, preserve the stored admission_fee — the saved value IS the
        // user's choice, even if it equals the default. Auto-fetch must not clobber it.
        if ((student.admission_fee || 0) > 0) {
          setAdmissionAmountManuallyEdited(true);
        }
        
        // Fetch course fee - will use course_id and session if available
        fetchCourseFee(student.course_id, student.session || null);
        
        // Fetch admission fee only if we have a valid session
        if (sessionObj) {
          console.log('   - Fetching admission fee with session ID:', sessionObj.id);
          fetchAdmissionFee(student.course_id, sessionObj.id, false); // false = not lateral
        } else {
          console.warn('   - ⚠️ Cannot fetch admission fee: No session found');
        }
      } else {
        console.warn('   - ⚠️ Cannot fetch fees: No course_id');
      }
    } catch (error) {
      console.error('❌ Error fetching student data:', error);
      alert('Failed to load student data for editing');
    }
  };

  // Handle document upload
  const handleDocumentUpload = (e, documentType) => {
    const file = e.target.files[0];
    if (file) {
      setDocuments(prev => ({
        ...prev,
        [documentType]: file
      }));
    }
  };

  const fetchBookingData = async () => {
    try {
      console.log('🎫 Fetching booking data for ID:', bookingId);
      const response = await api.get(`/seat-booking/${bookingId}`);
      const booking = response.data;
      setBookingData(booking);
      
      console.log('📦 Raw booking data:', booking);
      console.log('📦 online_application_number:', booking.online_application_number);
      console.log('📦 online_documents:', booking.online_documents ? Object.keys(booking.online_documents) : 'none');
      
      // Set documents from booking.online_documents (if API returned them directly)
      if (booking.online_documents && Object.keys(booking.online_documents).length > 0) {
        console.log('✅ Setting documents from booking.online_documents:', Object.keys(booking.online_documents));
        setDocuments(prev => ({
          ...prev,
          ...booking.online_documents
        }));
      }
      
      // If this booking came from online form, fetch the online application data for more details
      let onlineApplicationData = null;
      const onlineAppNumber = booking.online_application_number;
      
      if (onlineAppNumber) {
        try {
          console.log('🌐 Fetching online application data for:', onlineAppNumber);
          const onlineAppRes = await api.get(`/online-admissions/application/${onlineAppNumber}`);
          onlineApplicationData = onlineAppRes.data;
          console.log('📋 Online application data loaded:', onlineApplicationData);
          console.log('📄 Documents keys:', onlineApplicationData.documents ? Object.keys(onlineApplicationData.documents) : 'NO DOCUMENTS');
          
          // Set documents from online application (this will override/merge with online_documents)
          if (onlineApplicationData.documents && Object.keys(onlineApplicationData.documents).length > 0) {
            console.log('✅ Setting documents from online application:', Object.keys(onlineApplicationData.documents));
            setDocuments(prev => ({
              ...prev,
              ...onlineApplicationData.documents
            }));
          }
        } catch (err) {
          console.warn('⚠️ Could not fetch online application data:', err.message);
        }
      } else {
        // No online_application_number - try to find by phone/email (for older bookings)
        console.warn('⚠️ No online_application_number - trying to find by phone/email');
        try {
          const searchPhone = booking.phone || booking.whatsapp_no;
          const searchEmail = booking.email;
          if (searchPhone || searchEmail) {
            // Try to fetch online applications list and find matching one
            const onlineAppsRes = await api.get('/online-admissions/');
            const onlineApps = onlineAppsRes.data || [];
            const matchingApp = onlineApps.find(app => 
              (searchPhone && (app.phone === searchPhone || app.whatsapp_no === searchPhone)) ||
              (searchEmail && app.email === searchEmail)
            );
            if (matchingApp) {
              console.log('✅ Found matching online application:', matchingApp.application_number);
              onlineApplicationData = matchingApp;
              
              // Set documents from matched application
              if (matchingApp.documents && Object.keys(matchingApp.documents).length > 0) {
                console.log('✅ Setting documents from matched application:', Object.keys(matchingApp.documents));
                setDocuments(prev => ({
                  ...prev,
                  ...matchingApp.documents
                }));
              }
            }
          }
        } catch (err) {
          console.warn('⚠️ Could not search for online application:', err.message);
        }
      }
      
      // Fetch semesters for the course to auto-select 1st semester
      let firstSemesterId = '';
      if (booking.course_id) {
        try {
          const semestersRes = await api.get(`/master-settings/semesters?course_id=${booking.course_id}`);
          const semestersData = Array.isArray(semestersRes.data) ? semestersRes.data : (semestersRes.data?.data || []);
          setSemesters(semestersData);
          
          // For regular admission, find 1st semester
          const firstSemester = semestersData.find(s => s.semester_number === 1);
          if (firstSemester) {
            firstSemesterId = firstSemester.id;
            console.log('✅ Auto-selected 1st semester:', firstSemester.name, 'ID:', firstSemesterId);
          }
        } catch (err) {
          console.error('❌ Error fetching semesters:', err);
        }
      }
      
      // Pre-fill form with booking data
      // Handle both booking_amount and amount field names
      const bookingAmount = booking.booking_amount || booking.amount || 0;
      
      console.log('💰 CRITICAL: Booking amount extracted:', bookingAmount);
      console.log('   - booking.booking_amount:', booking.booking_amount);
      console.log('   - booking.amount:', booking.amount);
      
      // Merge online application data with booking data (online app has more details)
      const mergedData = onlineApplicationData ? {
        ...booking,
        // Personal Details from Online Application
        first_name: onlineApplicationData.first_name || booking.first_name || '',
        middle_name: onlineApplicationData.middle_name || booking.middle_name || '',
        last_name: onlineApplicationData.last_name || booking.last_name || '',
        email: onlineApplicationData.email || booking.email || '',
        phone: onlineApplicationData.phone || booking.phone || '',
        mobile: onlineApplicationData.mobile || '',
        whatsapp_no: onlineApplicationData.whatsapp_no || onlineApplicationData.mobile || '',
        date_of_birth: onlineApplicationData.date_of_birth || '',
        gender: onlineApplicationData.gender || '',
        religion: onlineApplicationData.religion || '',
        caste_category: onlineApplicationData.caste_category || onlineApplicationData.category || '',
        blood_group: onlineApplicationData.blood_group || '',
        aadhar_no: onlineApplicationData.aadhar_no || '',
        nationality: onlineApplicationData.nationality || 'Indian',
        
        // Family Details - Father
        father_name: onlineApplicationData.father_name || '',
        father_phone: onlineApplicationData.father_phone || '',
        father_email: onlineApplicationData.father_email || '',
        father_occupation: onlineApplicationData.father_occupation || '',
        
        // Family Details - Mother
        mother_name: onlineApplicationData.mother_name || '',
        mother_phone: onlineApplicationData.mother_phone || '',
        mother_email: onlineApplicationData.mother_email || '',
        mother_occupation: onlineApplicationData.mother_occupation || '',
        
        // Guardian Details
        guardian_name: onlineApplicationData.guardian_name || '',
        guardian_phone: onlineApplicationData.guardian_phone || '',
        guardian_relation: onlineApplicationData.guardian_relation || 'Father',
        
        // Current Address Details
        address: onlineApplicationData.address || '',
        city: onlineApplicationData.city || '',
        district: onlineApplicationData.district || '',
        state: onlineApplicationData.state || '',
        pin_code: onlineApplicationData.pin_code || '',
        landmark: onlineApplicationData.landmark || '',
        
        // Permanent Address Details
        permanent_address: onlineApplicationData.same_as_current ? onlineApplicationData.address : (onlineApplicationData.permanent_address || onlineApplicationData.address || ''),
        permanent_city: onlineApplicationData.same_as_current ? onlineApplicationData.city : (onlineApplicationData.permanent_city || onlineApplicationData.city || ''),
        permanent_state: onlineApplicationData.same_as_current ? onlineApplicationData.state : (onlineApplicationData.permanent_state || onlineApplicationData.state || ''),
        permanent_district: onlineApplicationData.same_as_current ? onlineApplicationData.district : (onlineApplicationData.permanent_district || onlineApplicationData.district || ''),
        permanent_pin_code: onlineApplicationData.same_as_current ? onlineApplicationData.pin_code : (onlineApplicationData.permanent_pin_code || onlineApplicationData.pin_code || ''),
        permanent_landmark: onlineApplicationData.same_as_current ? onlineApplicationData.landmark : (onlineApplicationData.permanent_landmark || onlineApplicationData.landmark || ''),
        
        // Academic Details
        class_10_board: onlineApplicationData.class_10_board || '',
        class_10_school: onlineApplicationData.class_10_school || '',
        class_10_year: onlineApplicationData.class_10_year || '',
        class_10_percentage: onlineApplicationData.class_10_percentage || '',
        class_12_board: onlineApplicationData.class_12_board || '',
        class_12_school: onlineApplicationData.class_12_school || '',
        class_12_year: onlineApplicationData.class_12_year || '',
        class_12_percentage: onlineApplicationData.class_12_percentage || '',
        
        // Diploma/Lateral Entry
        diploma_name: onlineApplicationData.diploma_name || '',
        diploma_college: onlineApplicationData.diploma_college || '',
        diploma_board: onlineApplicationData.diploma_board || '',
        diploma_year: onlineApplicationData.diploma_year || '',
        diploma_percentage: onlineApplicationData.diploma_percentage || '',
        
        // Higher Education
        graduation_degree: onlineApplicationData.graduation_degree || '',
        graduation_college: onlineApplicationData.graduation_college || '',
        graduation_university: onlineApplicationData.graduation_university || '',
        graduation_year: onlineApplicationData.graduation_year || '',
        graduation_percentage: onlineApplicationData.graduation_percentage || '',
      } : booking;
      
      setFormData(prev => {
        console.log('=== 🔄 SETTING FORM DATA FROM BOOKING ===');
        console.log('📊 Previous formData:', {
          seat_booking_amount: prev.seat_booking_amount,
          course_id: prev.course_id,
          session: prev.session
        });
        
        const newFormData = {
          ...prev,
          // Basic Info
          first_name: mergedData.first_name || '',
          middle_name: mergedData.middle_name || '',
          last_name: mergedData.last_name || '',
          email: mergedData.email || '',
          phone: mergedData.phone || '',
          whatsapp_no: mergedData.whatsapp_no || mergedData.mobile || '',
          date_of_birth: mergedData.date_of_birth || '',
          gender: mergedData.gender || '',
          religion: mergedData.religion || '',
          caste_category: mergedData.caste_category || '',
          blood_group: mergedData.blood_group || '',
          aadhar_no: mergedData.aadhar_no || '',
          nationality: mergedData.nationality || 'Indian',
          
          // Family Info - Father
          father_name: mergedData.father_name || '',
          father_phone: mergedData.father_phone || '',
          father_email: mergedData.father_email || '',
          father_occupation: mergedData.father_occupation || '',
          
          // Family Info - Mother
          mother_name: mergedData.mother_name || '',
          mother_phone: mergedData.mother_phone || '',
          mother_email: mergedData.mother_email || '',
          mother_occupation: mergedData.mother_occupation || '',
          
          // Guardian Info
          guardian_name: mergedData.guardian_name || '',
          guardian_phone: mergedData.guardian_phone || '',
          guardian_relation: mergedData.guardian_relation || 'Father',
          
          // Current Address
          address: mergedData.address || '',
          city: mergedData.city || '',
          district: mergedData.district || '',
          state: mergedData.state || '',
          pin_code: mergedData.pin_code || '',
          landmark: mergedData.landmark || '',
          
          // Permanent Address
          permanent_address: mergedData.permanent_address || mergedData.address || '',
          permanent_city: mergedData.permanent_city || mergedData.city || '',
          permanent_state: mergedData.permanent_state || mergedData.state || '',
          permanent_district: mergedData.permanent_district || mergedData.district || '',
          permanent_pin_code: mergedData.permanent_pin_code || mergedData.pin_code || '',
          permanent_landmark: mergedData.permanent_landmark || mergedData.landmark || '',
          
          // Academic
          class_10_board: mergedData.class_10_board || '',
          class_10_school: mergedData.class_10_school || '',
          class_10_year: mergedData.class_10_year || '',
          class_10_percentage: mergedData.class_10_percentage || '',
          class_12_board: mergedData.class_12_board || '',
          class_12_school: mergedData.class_12_school || '',
          class_12_year: mergedData.class_12_year || '',
          class_12_percentage: mergedData.class_12_percentage || '',
          
          // Diploma
          diploma_name: mergedData.diploma_name || '',
          diploma_college: mergedData.diploma_college || '',
          diploma_board: mergedData.diploma_board || '',
          diploma_year: mergedData.diploma_year || '',
          diploma_percentage: mergedData.diploma_percentage || '',
          
          // Graduation
          graduation_degree: mergedData.graduation_degree || '',
          graduation_college: mergedData.graduation_college || '',
          graduation_university: mergedData.graduation_university || '',
          graduation_year: mergedData.graduation_year || '',
          graduation_percentage: mergedData.graduation_percentage || '',
          has_graduation: !!mergedData.graduation_degree,
          
          // Course & Session - use session from online application or booking, fallback to current session
          course_id: booking.course_id || '',
          semester_id: firstSemesterId,
          session: onlineApplicationData?.session || booking.session || (sessions.find(s => s.is_current)?.name) || (sessions[0]?.name) || '',
          consultant_id: booking.consultant_id || '',
          seat_booking_amount: bookingAmount,
          is_lateral_entry: booking.is_lateral_entry || false,
          entry_semester: booking.is_lateral_entry ? (booking.entry_semester || 3) : 1,
          method_subject: onlineApplicationData?.method_subject || booking.method_subject || '',
          
          // Online Application Reference
          online_application_number: booking.online_application_number || ''
        };
        
        // If session is still empty, use current session
        if (!newFormData.session && sessions.length > 0) {
          const currentSession = sessions.find(s => s.is_current) || sessions[0];
          newFormData.session = currentSession?.name || '';
          console.log('✅ Session fallback to current:', newFormData.session);
        }
        
        console.log('📊 New formData:', {
          seat_booking_amount: newFormData.seat_booking_amount,
          course_id: newFormData.course_id,
          session: newFormData.session,
          semester_id: newFormData.semester_id,
          online_application_number: newFormData.online_application_number
        });
        console.log('💰 CRITICAL VERIFICATION: Booking amount is', newFormData.seat_booking_amount, 'and should be', bookingAmount);
        console.log('=== ✅ FORM DATA SET COMPLETE ===');
        
        return newFormData;
      });
      
      // Critical: Verify after state update (React batches state updates)
      console.log('⏰ Scheduled verification check...');
      
      // Add a verification setTimeout to check if the value persists
      setTimeout(() => {
        console.log('🔍 VERIFICATION CHECK (after 100ms):');
        console.log('Current formData.seat_booking_amount should be:', bookingAmount);
      }, 100);
      
      console.log('✅ Booking data loaded and form pre-filled');
      console.log('✅ 1st Semester auto-selected:', firstSemesterId);
      console.log('✅ Seat booking amount set to:', bookingAmount);
      if (onlineApplicationData) {
        console.log('✅ Online application data merged successfully');
      }
      
      // Fetch course fee and admission fee after booking data is loaded
      if (booking.course_id && booking.session) {
        console.log('💰 Fetching course fee for:', booking.course_id, 'session:', booking.session);
        fetchCourseFee(booking.course_id, booking.session);
        
        // IMPORTANT: Fetch document settings for this course
        console.log('📄 Fetching document settings for course:', booking.course_id);
        fetchDocumentSettings(booking.course_id);
        
        // Find session ID from session name for admission fee
        // Try exact match first, then try partial match (session might be stored as just year range)
        let sessionObj = sessions.find(s => s.name === booking.session || s.id === booking.session);
        if (!sessionObj && booking.session) {
          // Try to match by year range (e.g., "2025-2027" should match "2025-2027 (D Pharma Current)")
          sessionObj = sessions.find(s => s.name?.startsWith(booking.session));
          if (sessionObj) {
            console.log('✅ Found session by partial match:', sessionObj.name);
          }
        }
        
        if (sessionObj) {
          fetchAdmissionFee(booking.course_id, sessionObj.id, booking.is_lateral_entry || false);
        } else {
          console.warn('⚠️ Session object not found for:', booking.session, 'Will retry after sessions load');
          console.warn('⚠️ Available sessions:', sessions.map(s => s.name));
          // Retry after a delay to allow sessions to load
          setTimeout(() => {
            let retrySessionObj = sessions.find(s => s.name === booking.session || s.id === booking.session);
            if (!retrySessionObj && booking.session) {
              retrySessionObj = sessions.find(s => s.name?.startsWith(booking.session));
            }
            if (retrySessionObj) {
              console.log('✅ Retry successful! Found session:', retrySessionObj.name);
              fetchAdmissionFee(booking.course_id, retrySessionObj.id, booking.is_lateral_entry || false);
            } else {
              console.error('❌ Still cannot find session after retry. Sessions:', sessions.map(s => s.name));
            }
          }, 1000);
        }
      } else if (booking.course_id) {
        console.log('💰 Fetching course fee without session:', booking.course_id);
        fetchCourseFee(booking.course_id);
        
        // IMPORTANT: Fetch document settings for this course
        console.log('📄 Fetching document settings for course:', booking.course_id);
        fetchDocumentSettings(booking.course_id);
      }
      
      // Consultant payout will auto-calculate via useEffect after fees are loaded
      console.log('👔 Consultant from booking will auto-trigger payout calculation:', booking.consultant_id);
    } catch (error) {
      console.error('❌ Error fetching booking data:', error);
      alert('Failed to load booking data. Please try again.');
    }
  };

  // Fetch online application data when navigating from Admission Hub with online_application parameter
  const fetchOnlineApplicationData = async () => {
    try {
      console.log('🌐 Fetching online application data for ID:', onlineApplicationId);
      const response = await api.get(`/online-admissions/${onlineApplicationId}`);
      const application = response.data;
      setOnlineApplicationData(application);
      
      console.log('📦 Online application data:', application);
      
      // Set documents from online application
      if (application.documents) {
        console.log('📄 Documents from online application:', Object.keys(application.documents));
        setDocuments(prev => ({
          ...prev,
          ...application.documents
        }));
      }
      
      // Fetch semesters for the course
      let firstSemesterId = '';
      if (application.course_id) {
        try {
          const semestersRes = await api.get(`/master-settings/semesters?course_id=${application.course_id}`);
          const semestersData = Array.isArray(semestersRes.data) ? semestersRes.data : (semestersRes.data?.data || []);
          setSemesters(semestersData);
          
          const firstSemester = semestersData.find(s => s.semester_number === 1);
          if (firstSemester) {
            firstSemesterId = firstSemester.id;
            console.log('✅ Auto-selected 1st semester:', firstSemester.name);
          }
        } catch (err) {
          console.error('❌ Error fetching semesters:', err);
        }
      }
      
      // Pre-fill form with online application data
      const bookingAmount = application.amount || application.booking_amount || 0;
      
      setFormData(prev => ({
        ...prev,
        // Basic Info
        first_name: application.first_name || '',
        middle_name: application.middle_name || '',
        last_name: application.last_name || '',
        email: application.email || '',
        phone: application.phone || application.mobile || '',
        whatsapp_no: application.whatsapp_no || application.mobile || '',
        date_of_birth: application.date_of_birth || '',
        gender: application.gender || '',
        religion: application.religion || '',
        caste_category: application.caste_category || application.category || '',
        blood_group: application.blood_group || '',
        aadhar_no: application.aadhar_no || '',
        nationality: application.nationality || 'Indian',
        
        // Family Info
        father_name: application.father_name || '',
        mother_name: application.mother_name || '',
        guardian_name: application.guardian_name || '',
        guardian_phone: application.guardian_phone || '',
        guardian_occupation: application.guardian_occupation || '',
        
        // Address
        address: application.address || '',
        city: application.city || '',
        district: application.district || '',
        state: application.state || '',
        pin_code: application.pin_code || '',
        permanent_address: application.permanent_address || application.address || '',
        permanent_city: application.permanent_city || application.city || '',
        permanent_district: application.permanent_district || application.district || '',
        permanent_state: application.permanent_state || application.state || '',
        permanent_pin_code: application.permanent_pin_code || application.pin_code || '',
        
        // Academic
        class_10_board: application.class_10_board || '',
        class_10_school: application.class_10_school || '',
        class_10_year: application.class_10_year || '',
        class_10_percentage: application.class_10_percentage || '',
        class_12_board: application.class_12_board || '',
        class_12_school: application.class_12_school || '',
        class_12_year: application.class_12_year || '',
        class_12_percentage: application.class_12_percentage || '',
        
        // Diploma
        diploma_name: application.diploma_name || '',
        diploma_college: application.diploma_college || '',
        diploma_board: application.diploma_board || '',
        diploma_year: application.diploma_year || '',
        diploma_percentage: application.diploma_percentage || '',
        
        // Graduation
        graduation_degree: application.graduation_degree || '',
        graduation_college: application.graduation_college || '',
        graduation_university: application.graduation_university || '',
        graduation_year: application.graduation_year || '',
        graduation_percentage: application.graduation_percentage || '',
        has_graduation: !!application.graduation_degree,
        
        // Course & Session
        course_id: application.course_id || '',
        semester_id: firstSemesterId,
        session: application.session || '',
        seat_booking_amount: bookingAmount,
        is_lateral_entry: application.is_lateral_entry || false,
        entry_semester: application.is_lateral_entry ? (application.entry_semester || 3) : 1,
        method_subject: application.method_subject || '',
        
        // Online Application Reference
        online_application_number: application.application_number || ''
      }));
      
      console.log('✅ Online application data loaded and form pre-filled');
      
      // Fetch fees
      if (application.course_id && application.session) {
        fetchCourseFee(application.course_id, application.session);
        
        let sessionObj = sessions.find(s => s.name === application.session || s.id === application.session);
        if (!sessionObj && application.session) {
          sessionObj = sessions.find(s => s.name?.startsWith(application.session));
        }
        
        if (sessionObj) {
          fetchAdmissionFee(application.course_id, sessionObj.id, application.is_lateral_entry || false);
        }
      }
    } catch (error) {
      console.error('❌ Error fetching online application data:', error);
      alert('Failed to load online application data. Please try again.');
    }
  };

  const fetchMasterData = async () => {
    try {
      console.log('🔄 Fetching all master data...');
      const [coursesRes, sectionsRes, consultantsRes, sessionsRes, discountsRes, scholarshipsRes, collegeInfoRes, tenantInfoRes] = await Promise.all([
        api.get('/master-settings/courses'),
        api.get('/master-settings/sections'),
        api.get('/consultants/'),
        api.get('/master-settings/sessions'),
        api.get('/master-settings/discounts'),
        api.get('/master-settings/scholarships'),
        api.get('/master-settings/college-info').catch(() => ({ data: { college_code: '' } })),
        api.get('/tenants/my-college/settings').catch(() => ({ data: null }))
      ]);
      
      // Helper function to safely extract array data
      const safeArray = (data) => Array.isArray(data) ? data : (data?.data || []);
      
      const coursesData = safeArray(coursesRes.data);
      const sectionsData = safeArray(sectionsRes.data);
      const consultantsData = safeArray(consultantsRes.data);
      const sessionsData = safeArray(sessionsRes.data);
      const discountsData = safeArray(discountsRes.data);
      const scholarshipsData = safeArray(scholarshipsRes.data);
      
      setCourses(coursesData);
      setSections(sectionsData);
      setConsultants(consultantsData.filter(c => !c.is_blocked));
      setSessions(sessionsData);
      setDiscounts(discountsData.filter(d => d.entry_type === 'Regular' || !d.entry_type));
      setScholarships(scholarshipsData.filter(s => s.entry_type === 'Regular' || !s.entry_type));
      
      // Merge college-info (for college_code) with tenant info (for name, logo, address)
      const mergedCollegeInfo = {
        ...(collegeInfoRes.data || { college_code: '' }),
        ...(tenantInfoRes.data || {}),
        college_name: tenantInfoRes.data?.name || tenantInfoRes.data?.college_name,
        logo_url: tenantInfoRes.data?.logo || tenantInfoRes.data?.logo_url
      };
      setCollegeInfo(mergedCollegeInfo);
      
      console.log('✅ Master data loaded:');
      console.log('   - Courses:', coursesData.length);
      console.log('   - Sections:', sectionsData.length);
      console.log('   - Consultants:', consultantsData.filter(c => !c.is_blocked).length);
      console.log('   - Sessions:', sessionsData.length, sessionsData.map(s => ({ id: s.id, name: s.name })));
      console.log('   - Discounts:', discountsData.filter(d => d.entry_type === 'Regular' || !d.entry_type).length);
      console.log('   - Scholarships:', scholarshipsData.filter(s => s.entry_type === 'Regular' || !s.entry_type).length);
      console.log('   - College Code:', collegeInfoRes.data?.college_code || 'NOT SET');
      console.log('   - Tenant Info:', tenantInfoRes.data?.name || 'NOT LOADED');
      console.log('   - Languages/Religions/Castes: lazy-loaded on field focus');
    } catch (error) {
      console.error('❌ Error fetching master data:', error);
    }
  };

  // Lazy-load less-critical dropdowns ONLY when user interacts with them.
  // Reduces initial page load fan-out from 11 → 8 API calls (~27% faster TTFB).
  const lazyLoadedRef = React.useRef({ languages: false, religions: false, castes: false });
  const ensureLanguagesLoaded = async () => {
    if (lazyLoadedRef.current.languages) return;
    lazyLoadedRef.current.languages = true;
    try {
      const res = await api.get('/master-settings/languages');
      setLanguages(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (e) { lazyLoadedRef.current.languages = false; }
  };
  const ensureReligionsLoaded = async () => {
    if (lazyLoadedRef.current.religions) return;
    lazyLoadedRef.current.religions = true;
    try {
      const res = await api.get('/master-settings/religions');
      setReligions(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (e) { lazyLoadedRef.current.religions = false; }
  };
  const ensureCastesLoaded = async () => {
    if (lazyLoadedRef.current.castes) return;
    lazyLoadedRef.current.castes = true;
    try {
      const res = await api.get('/master-settings/castes');
      setCastes(Array.isArray(res.data) ? res.data : (res.data?.data || []));
    } catch (e) { lazyLoadedRef.current.castes = false; }
  };

  const handleConsultantChange = (consultantId) => {
    setFormData({ ...formData, consultant_id: consultantId });
    // Consultant payout will auto-calculate via useEffect
  };

  const handleAdmissionFeeChange = (fee) => {
    // This is just for backward compatibility - consultant payout is now based on course fee
    setFormData({ ...formData, admission_fee: parseFloat(fee) || 0 });
  };

  const handleFileChange = (fieldName, file, docSetting) => {
    if (file) {
      // Use actual max_size_kb from document settings, fallback to 500KB
      const maxSizeKB = docSetting?.max_size_kb || 300;
      const maxSizeBytes = maxSizeKB * 1024;
      if (file.size > maxSizeBytes) {
        alert(`File size should not exceed ${maxSizeKB}KB. Selected file is ${(file.size / 1024).toFixed(0)}KB`);
        return;
      }
      setDocuments({ ...documents, [fieldName]: file });
    }
  };

  const handleRemoveFile = (fieldName) => {
    setDocuments({ ...documents, [fieldName]: null });
  };

  const fetchSemesters = async (courseId, isLateral = null) => {
    try {
      const response = await api.get('/master-settings/semesters', {
        params: { course_id: courseId }
      });
      const fetchedSemesters = response.data;
      setSemesters(fetchedSemesters);
      
      // Auto-select appropriate semester based on entry type
      // Use passed parameter or fall back to formData
      const isLateralEntry = isLateral !== null ? isLateral : formData.is_lateral_entry;
      
      if (fetchedSemesters && fetchedSemesters.length > 0) {
        let targetSemester = null;
        
        if (isLateralEntry) {
          // For lateral entry, find 3rd semester
          targetSemester = fetchedSemesters.find(s => 
            s.name === 'Semester 3' || 
            s.name === '3' || 
            s.semester_number === 3 ||
            s.name.includes('3')
          );
          console.log('📚 Auto-selecting 3rd semester for lateral entry:', targetSemester?.name);
        } else {
          // For regular entry, find 1st semester
          targetSemester = fetchedSemesters.find(s => 
            s.name === 'Semester 1' || 
            s.name === '1' || 
            s.semester_number === 1 ||
            s.name.includes('1')
          );
          console.log('📚 Auto-selecting 1st semester for regular entry:', targetSemester?.name);
        }
        
        // If target semester found, auto-select it
        if (targetSemester) {
          setFormData(prev => ({
            ...prev,
            semester_id: targetSemester.id
          }));
          
          // Trigger admission fee fetch if session is already selected
          if (formData.session) {
            const sessionObj = sessions.find(s => s.name === formData.session);
            if (sessionObj) {
              fetchAdmissionFee(courseId, sessionObj.id, isLateralEntry);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error fetching semesters:', error);
    }
  };

  // Fetch document settings for selected course
  const fetchDocumentSettings = async (courseId) => {
    if (!courseId) {
      console.log('Cannot fetch document settings - missing courseId');
      return;
    }
    
    setDocumentSettingsLoading(true);
    try {
      // include_defaults=true ensures we get default documents if none are configured
      const response = await api.get(`/document-settings/?course_id=${courseId}&include_defaults=true`);
      const docs = response.data || [];
      console.log('📄 Document settings loaded:', docs.length, 'documents');
      
      // If API returns empty, use default documents
      if (docs.length === 0) {
        console.log('📄 Using default documents as fallback');
        const defaultDocs = [
          {id: "default_1", document_name: "Passport Photo", is_mandatory: true, allowed_formats: ["jpg", "jpeg", "png"], max_size_kb: 200, description: "Recent passport size photo"},
          {id: "default_2", document_name: "10th Marksheet", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
          {id: "default_3", document_name: "10th Certificate", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
          {id: "default_4", document_name: "12th Marksheet", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
          {id: "default_5", document_name: "12th Certificate", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
          {id: "default_6", document_name: "Aadhar Card", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 300, description: ""},
          {id: "default_7", document_name: "Caste Certificate", is_mandatory: false, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: "If applicable"},
          {id: "default_8", document_name: "Income Certificate", is_mandatory: false, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: "If applicable"},
          {id: "default_9", document_name: "Domicile Certificate", is_mandatory: false, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: "If applicable"},
        ];
        setDocumentSettings(defaultDocs);
      } else {
        setDocumentSettings(docs);
      }
    } catch (error) {
      console.error('Error fetching document settings:', error);
      // Use default documents on error
      const defaultDocs = [
        {id: "default_1", document_name: "Passport Photo", is_mandatory: true, allowed_formats: ["jpg", "jpeg", "png"], max_size_kb: 200, description: "Recent passport size photo"},
        {id: "default_2", document_name: "10th Marksheet", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
        {id: "default_3", document_name: "10th Certificate", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
        {id: "default_4", document_name: "12th Marksheet", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
        {id: "default_5", document_name: "12th Certificate", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: ""},
        {id: "default_6", document_name: "Aadhar Card", is_mandatory: true, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 300, description: ""},
        {id: "default_7", document_name: "Caste Certificate", is_mandatory: false, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: "If applicable"},
        {id: "default_8", document_name: "Income Certificate", is_mandatory: false, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: "If applicable"},
        {id: "default_9", document_name: "Domicile Certificate", is_mandatory: false, allowed_formats: ["pdf", "jpg", "jpeg", "png"], max_size_kb: 500, description: "If applicable"},
      ];
      setDocumentSettings(defaultDocs);
    } finally {
      setDocumentSettingsLoading(false);
    }
  };

  const fetchAdmissionFee = async (courseId, sessionId, isLateral = false) => {
    console.log(`🔍 fetchAdmissionFee called with:`, {
      courseId,
      sessionId,
      isLateral,
      formData_session: formData.session
    });
    try {
      // First try with both course_id and session_id
      let response = await api.get('/admission-fee-settings/', {
        params: { course_id: courseId, session_id: sessionId }
      });
      
      console.log(`📡 Admission fee API response:`, response.data);
      
      // If no results with session_id, try with just course_id
      if (!response.data || response.data.length === 0) {
        console.log(`⚠️ No fee found with session_id, trying with just course_id...`);
        response = await api.get('/admission-fee-settings/', {
          params: { course_id: courseId }
        });
        console.log(`📡 Admission fee API response (course only):`, response.data);
      }
      
      const feeData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      
      if (feeData && feeData.length > 0) {
        const feeSetting = feeData[0];
        console.log(`📊 Fee settings available:`, {
          regular: feeSetting.regular_admission_fee,
          lateral: feeSetting.lateral_admission_fee,
          using: isLateral ? 'lateral' : 'regular'
        });
        const admissionAmount = isLateral ? feeSetting.lateral_admission_fee : feeSetting.regular_admission_fee;
        setFormData(prev => {
          // 🔒 Preserve user's manual edit to Admission Fee — don't overwrite once they've typed
          if (admissionAmountManuallyEdited) {
            console.log(`✋ Skipping admission fee auto-update — user manually edited (kept ₹${prev.admission_amount})`);
            return prev;
          }
          return {
            ...prev,
            admission_amount: admissionAmount || 0,
            admission_fee: admissionAmount || 0  // Also set admission_fee for backward compatibility
          };
        });
        console.log(`✓ Admission fee set: ₹${admissionAmount} (${isLateral ? 'Lateral Entry' : 'Regular Entry'}) | Manual edit preserved: ${admissionAmountManuallyEdited}`);
        // Balance fee will auto-update via useEffect
      } else {
        console.warn(`⚠️ No admission fee settings found for course_id=${courseId}`);
      }
    } catch (error) {
      console.error('❌ Error fetching admission fee:', error.response?.data || error.message);
    }
  };

  const fetchCourseFee = async (courseId, sessionName = null, isLateral = null) => {
    console.log(`🔍 fetchCourseFee called with:`, {
      courseId,
      sessionName,
      isLateral,
      formData_isLateral: formData.is_lateral_entry
    });
    try {
      const response = await api.get('/master-settings/fee-structures', {
        params: { course_id: courseId }
      });
      
      const feeStructuresData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      console.log(`📡 Course fee API response: ${feeStructuresData.length} fee structures`);
      
      if (feeStructuresData && feeStructuresData.length > 0) {
        // CRITICAL: Filter by entry type based on current form selection or passed parameter
        const isLateralEntry = isLateral !== null ? isLateral : formData.is_lateral_entry;
        console.log(`🔍 Fetching course fee for ${isLateralEntry ? 'LATERAL' : 'REGULAR'} entry`);
        
        let feeStructures = feeStructuresData.filter(f => {
          // Filter based on is_lateral_entry field
          if (f.is_lateral_entry === true && !isLateralEntry) {
            return false; // Exclude lateral fees when in regular mode
          }
          if (f.is_lateral_entry === false && isLateralEntry) {
            return false; // Exclude regular fees when in lateral mode
          }
          // Also check semester count as backup (Regular = 8, Lateral = 6)
          if (isLateralEntry && f.total_semesters && f.total_semesters === 8) {
            return false; // Exclude 8-semester fees in lateral mode
          }
          if (!isLateralEntry && f.total_semesters && f.total_semesters === 6) {
            return false; // Exclude 6-semester fees in regular mode
          }
          return true; // Include this fee structure
        });
        
        if (feeStructures.length === 0) {
          console.warn(`⚠️ No ${isLateralEntry ? 'Lateral' : 'Regular'} Entry fee structures found, using all`);
          feeStructures = response.data;
        }
        
        // CRITICAL FIX: Sort fee structures by total_fee descending to get the highest/most current fee
        // This prevents selecting old fee structures with lower amounts
        feeStructures.sort((a, b) => {
          const feeA = a.total_fee || a.total_amount || 0;
          const feeB = b.total_fee || b.total_amount || 0;
          return feeB - feeA; // Descending order (highest first)
        });
        
        let feeStructure = feeStructures[0];
        console.log(`📋 Found ${feeStructures.length} fee structures, selected highest fee: ₹${feeStructure?.total_fee || feeStructure?.total_amount || 0}`);
        
        if (sessionName) {
          // Try to find fee structure matching the session
          const matchingFee = feeStructures.find(f => 
            f.name && f.name.includes(sessionName)
          );
          if (matchingFee) {
            feeStructure = matchingFee;
            console.log(`✅ Matched ${isLateralEntry ? 'LATERAL' : 'REGULAR'} fee structure for session ${sessionName}: ${feeStructure.name}`);
          } else {
            console.log(`⚠️ No exact session match, using highest fee structure: ${feeStructure.name}`);
          }
        }
        
        const courseFee = (
          // Prefer entry-type-specific fee field (newer fee structures persist
          // `regular_fee` / `lateral_fee` explicitly and may leave `total_fee` null).
          isLateralEntry
            ? (feeStructure.lateral_fee || feeStructure.total_fee || feeStructure.total_amount || feeStructure.regular_fee || 0)
            : (feeStructure.regular_fee || feeStructure.total_fee || feeStructure.total_amount || feeStructure.lateral_fee || 0)
        );
        if (!courseFee || courseFee <= 0) {
          console.warn(`⚠️ Fee structure "${feeStructure.name}" found but resolved fee amount is 0. Check that regular_fee/lateral_fee/total_fee fields are populated.`);
        }
        setFormData(prev => ({ 
          ...prev, 
          total_course_fee: courseFeeManuallyEdited ? prev.total_course_fee : courseFee
        }));
        console.log(`💰 Course fee fetched: ₹${courseFee} for ${feeStructure.name} (Entry Type: ${isLateralEntry ? 'Lateral' : 'Regular'}) | Manual edit preserved: ${courseFeeManuallyEdited}`);
        // Consultant payout and balance fee will auto-update via useEffect
      }
    } catch (error) {
      console.error('Error fetching course fee:', error);
    }
  };

  const generateAdmissionReceiptNumber = () => {
    const year = new Date().getFullYear().toString().slice(-2);
    const random = Math.floor(Math.random() * 100000).toString().padStart(5, '0');
    return `ADMRCT${year}${random}`;
  };

  // Deprecated: Balance fee is now auto-calculated via useEffect
  const calculateBalanceFee = () => {
    // This function is kept for backward compatibility but is no longer needed
    // Balance fee is now automatically calculated when any relevant value changes
  };

  const addDiscount = (discountId) => {
    if (!discountId || appliedDiscounts.some(d => d.id === discountId)) return;
    
    const discount = discounts.find(d => d.id === discountId);
    if (discount) {
      const newAppliedDiscounts = [...appliedDiscounts, discount];
      setAppliedDiscounts(newAppliedDiscounts);
      
      const totalDiscount = newAppliedDiscounts.reduce((sum, d) => sum + (d.value || d.amount || 0), 0);
      setFormData(prev => ({ 
        ...prev, 
        discount_amount: totalDiscount,
        applied_discounts: newAppliedDiscounts.map(d => d.id)
      }));
      // Balance fee will auto-update via useEffect
    }
  };

  const removeDiscount = (discountId) => {
    const newAppliedDiscounts = appliedDiscounts.filter(d => d.id !== discountId);
    setAppliedDiscounts(newAppliedDiscounts);
    
    const totalDiscount = newAppliedDiscounts.reduce((sum, d) => sum + (d.value || d.amount || 0), 0);
    setFormData(prev => ({ 
      ...prev, 
      discount_amount: totalDiscount,
      applied_discounts: newAppliedDiscounts.map(d => d.id)
    }));
    // Balance fee will auto-update via useEffect
  };

  const addScholarship = (scholarshipId) => {
    if (!scholarshipId || appliedScholarships.some(s => s.id === scholarshipId)) return;
    
    const scholarship = scholarships.find(s => s.id === scholarshipId);
    if (scholarship) {
      const newAppliedScholarships = [...appliedScholarships, scholarship];
      setAppliedScholarships(newAppliedScholarships);
      
      const totalScholarship = newAppliedScholarships.reduce((sum, s) => sum + (s.value || s.amount || 0), 0);
      setFormData(prev => ({ 
        ...prev, 
        scholarship_amount: totalScholarship,
        applied_scholarships: newAppliedScholarships.map(s => s.id)
      }));
      // Balance fee will auto-update via useEffect
    }
  };

  const removeScholarship = (scholarshipId) => {
    const newAppliedScholarships = appliedScholarships.filter(s => s.id !== scholarshipId);
    setAppliedScholarships(newAppliedScholarships);
    
    const totalScholarship = newAppliedScholarships.reduce((sum, s) => sum + (s.value || s.amount || 0), 0);
    setFormData(prev => ({ 
      ...prev, 
      scholarship_amount: totalScholarship,
      applied_scholarships: newAppliedScholarships.map(s => s.id)
    }));
    // Balance fee will auto-update via useEffect
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Validate phone number fields to accept only numbers
    const phoneFields = ['phone', 'whatsapp_no', 'father_phone', 'mother_phone', 'guardian_phone'];
    if (phoneFields.includes(name)) {
      // Allow only digits, limit to 10-15 characters
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 15) {
        setFormData(prev => ({
          ...prev,
          [name]: numericValue
        }));
      }
      return;
    }
    
    setFormData(prev => ({ ...prev, [name]: value }));
    
    if (name === 'course_id' && value) {
      // Find selected course data for lateral entry check
      const courseData = courses.find(c => c.id === value);
      setSelectedCourseData(courseData);
      
      // Reset lateral entry if course doesn't support it
      if (courseData && !courseData.lateral_entry_allowed) {
        setFormData(prev => ({
          ...prev,
          course_id: value,
          is_lateral_entry: false,
          entry_semester: 1
        }));
      }
      
      fetchSemesters(value);
      
      // Auto-select active session for this course (always when course changes)
      const activeSession = sessions.find(s => s.is_current) || sessions[0];
      console.log('🔍 Sessions available:', sessions.length, 'Active session:', activeSession?.name);
      
      if (activeSession) {
        // Update course_id and session together
        setFormData(prev => ({
          ...prev,
          course_id: value,
          session: activeSession.name
        }));
        console.log('✅ Auto-selected session:', activeSession.name);
        
        // Fetch fees with auto-selected session
        fetchCourseFee(value, activeSession.name);
        fetchAdmissionFee(value, activeSession.id, formData.is_lateral_entry);
      } else {
        fetchCourseFee(value);
      }
      
      // Fetch document settings for this course
      fetchDocumentSettings(value);
      
      // Consultant payout and balance fee will auto-update via useEffect
    }
    
    if (name === 'semester_id' && value) {
      // Fetch admission fee when semester is selected (if course and session already selected)
      if (formData.course_id && formData.session) {
        const sessionObj = sessions.find(s => s.name === formData.session);
        if (sessionObj) {
          fetchAdmissionFee(formData.course_id, sessionObj.id, formData.is_lateral_entry);
        }
      }
    }
    
    if (name === 'session' && value) {
      console.log(`📅 Session changed to: ${value}`);
      console.log(`📋 Current formData.is_lateral_entry:`, formData.is_lateral_entry);
      console.log(`📋 Available sessions:`, sessions.map(s => ({ id: s.id, name: s.name })));
      
      // ⚠️ CRITICAL: Use updated formData by accessing it in the callback
      setFormData(prev => {
        const currentIsLateral = prev.is_lateral_entry;
        console.log(`🔍 Current lateral entry status from state:`, currentIsLateral);
        
        // Re-fetch fees with current lateral status
        if (prev.course_id) {
          console.log(`🔄 Fetching fees for course: ${prev.course_id}, session: ${value}, isLateral: ${currentIsLateral}`);
          fetchCourseFee(prev.course_id, value, currentIsLateral);
          
          const sessionObj = sessions.find(s => s.name === value);
          if (sessionObj) {
            console.log(`✓ Found session, calling fetchAdmissionFee with:`, {
              courseId: prev.course_id,
              sessionId: sessionObj.id,
              isLateral: currentIsLateral
            });
            fetchAdmissionFee(prev.course_id, sessionObj.id, currentIsLateral);
          } else {
            console.warn(`⚠️ Session object not found for name="${value}"`);
          }
        }
        
        return prev; // Don't change formData here, just use it for side effects
      });
      
      // Balance fee will auto-update via useEffect
    }
  };

  const handleSameAsPermanent = (e) => {
    const isChecked = e.target.checked;
    setSameAsPermanent(isChecked);
    
    if (isChecked) {
      // Copy permanent address to current address
      setFormData(prev => ({
        ...prev,
        address: prev.permanent_address,
        city: prev.permanent_city,
        state: prev.permanent_state,
        district: prev.permanent_district,
        pin_code: prev.permanent_pin_code,
        landmark: prev.permanent_landmark
      }));
    }
  };

  const convertFileToBase64 = async (file) => {
    // Use smart reader: auto-compresses images, passes PDFs through.
    // Slashes 2-5 MB photo uploads down to ~150-400 KB.
    try {
      const { readFileSmart } = await import('@/utils/imageCompressor');
      const result = await readFileSmart(file);
      return result.data;
    } catch (e) {
      console.warn('Smart read failed, falling back to raw FileReader:', e);
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.whatsapp_no) {
      alert('WhatsApp number is required');
      return;
    }
    
    // Validate course selection
    if (!formData.course_id) {
      alert('Please select a course');
      return;
    }
    
    // Check if Fee Structure exists for the selected course
    if (!formData.total_course_fee || formData.total_course_fee <= 0) {
      // 🛡️ Race-condition safety net: maybe fetchCourseFee hadn't finished by
      // the time the user clicked Submit. Make ONE synchronous retry against
      // the API and check the result before showing the error.
      try {
        const sessionsList = sessions.filter(s =>
          (s.course_id && s.course_id === formData.course_id) || !s.course_id
        );
        const activeSession = sessionsList.find(s => s.is_current) || sessionsList[0];
        const sessionName = activeSession?.name || formData.session || null;
        const resp = await api.get(`/master-settings/fee-structures`, {
          params: { course_id: formData.course_id },
        });
        const data = Array.isArray(resp.data) ? resp.data : [];
        const isLat = !!formData.is_lateral_entry;
        const filtered = data.filter(f => {
          if (f.is_lateral_entry === true && !isLat) return false;
          if (f.is_lateral_entry === false && isLat) return false;
          if (isLat && f.total_semesters === 8) return false;
          if (!isLat && f.total_semesters === 6) return false;
          return true;
        });
        if (filtered.length > 0) {
          const sorted = filtered.sort((a, b) => (b.total_fee || b.regular_fee || 0) - (a.total_fee || a.regular_fee || 0));
          let pick = sorted[0];
          if (sessionName) {
            const m = sorted.find(f => f.name && f.name.includes(sessionName));
            if (m) pick = m;
          }
          const retryFee = isLat
            ? (pick.lateral_fee || pick.total_fee || pick.total_amount || pick.regular_fee || 0)
            : (pick.regular_fee || pick.total_fee || pick.total_amount || pick.lateral_fee || 0);
          if (retryFee > 0) {
            // Re-fetched successfully; continue submission with this value.
            formData.total_course_fee = retryFee;
            setFormData(prev => ({ ...prev, total_course_fee: retryFee }));
            console.log(`🔁 Fee re-fetched at submit time: ₹${retryFee}`);
          }
        }
      } catch (e) {
        console.warn('Fee re-fetch on submit failed:', e);
      }

      // If retry still didn't help, show the error
      if (!formData.total_course_fee || formData.total_course_fee <= 0) {
        const courseName = courses.find(c => c.id === formData.course_id)?.name || 'this course';
        alert(`Fee Structure not configured for "${courseName}". Please set up Fee Structure in Master Settings → Fee Structures before admitting students to this course.`);
        return;
      }
    }
    
    // Validate admission payment modes
    if (formData.admission_amount > 0) {
      if (!formData.admission_payment_modes || formData.admission_payment_modes.length === 0) {
        alert('Please add at least one payment mode for admission fee');
        return;
      }
      
      const totalPaid = formData.admission_payment_modes.reduce((sum, mode) => sum + parseFloat(mode.amount || 0), 0);
      if (Math.abs(totalPaid - formData.admission_amount) > 0.01) {
        alert(`Admission payment breakdown total (₹${totalPaid.toLocaleString('en-IN')}) must equal admission fee (₹${formData.admission_amount.toLocaleString('en-IN')})`);
        return;
      }
    }
    
    setLoading(true);
    try {
      // Convert all uploaded documents to base64
      const uploadedDocuments = {};
      for (const [key, value] of Object.entries(documents)) {
        if (value) {
          try {
            // Check if it's already a base64 string (from online application)
            if (typeof value === 'string' && (value.startsWith('data:') || value.startsWith('http'))) {
              // Already base64 or URL - use as-is
              uploadedDocuments[key] = {
                data: value,
                filename: `${key}.jpg`,
                mimetype: value.startsWith('data:image/png') ? 'image/png' : 
                          value.startsWith('data:application/pdf') ? 'application/pdf' : 'image/jpeg',
                size: 0,
                from_online_application: true
              };
            } else if (value instanceof File) {
              // It's a File object - convert to base64
              const base64Data = await convertFileToBase64(value);
              uploadedDocuments[key] = {
                data: base64Data,
                filename: value.name,
                mimetype: value.type,
                size: value.size
              };
            } else if (typeof value === 'object' && value.data) {
              // It's already a document object with data
              uploadedDocuments[key] = value;
            }
          } catch (err) {
            console.error(`Error converting ${key} to base64:`, err);
          }
        }
      }
      
      console.log('📄 Final uploadedDocuments:', Object.keys(uploadedDocuments));
      
      // Transform formData to match backend StudentCreate model
      const full_name = [formData.first_name, formData.middle_name, formData.last_name]
        .filter(Boolean)
        .join(' ');
      
      const submissionData = {
        admission_number: formData.admission_no,
        roll_number: formData.roll_number,
        university_id: formData.university_id,
        method_subject: formData.method_subject,
        full_name: full_name,
        email: formData.email,
        phone: formData.phone,
        whatsapp_no: formData.whatsapp_no,
        date_of_birth: formData.date_of_birth,
        gender: formData.gender,
        nationality: formData.nationality,
        aadhar_no: formData.aadhar_no,
        // Address Information
        address: formData.address,
        city: formData.city,
        state: formData.state,
        district: formData.district,
        pin_code: formData.pin_code,
        landmark: formData.landmark,
        // Permanent Address Information
        permanent_address: formData.permanent_address,
        permanent_city: formData.permanent_city,
        permanent_state: formData.permanent_state,
        permanent_district: formData.permanent_district,
        permanent_pin_code: formData.permanent_pin_code,
        permanent_landmark: formData.permanent_landmark,
        // Academic Details
        class_10_board: formData.class_10_board,
        class_10_year: formData.class_10_year,
        class_10_percentage: formData.class_10_percentage,
        class_12_board: formData.class_12_board,
        class_12_year: formData.class_12_year,
        class_12_percentage: formData.class_12_percentage,
        // Course Information
        course_id: formData.course_id,
        semester_id: formData.semester_id,
        section_id: formData.section_id,
        session: formData.session,
        admission_date: formData.admission_date,
        // Father's Information
        father_name: formData.father_name,
        father_phone: formData.father_phone,
        father_email: formData.father_email,
        father_occupation: formData.father_occupation,
        // Mother's Information
        mother_name: formData.mother_name,
        mother_phone: formData.mother_phone,
        mother_email: formData.mother_email,
        mother_occupation: formData.mother_occupation,
        // Guardian Information
        guardian_name: formData.guardian_name,
        guardian_phone: formData.guardian_phone,
        guardian_relation: formData.guardian_relation,
        // Other Details
        blood_group: formData.blood_group,
        religion: formData.religion,
        caste: formData.caste_category,
        consultant_id: formData.consultant_id,
        consultant_payout: consultantPayout || 0,
        // Fee Information
        admission_fee: formData.admission_amount,
        admission_payment_mode: formData.admission_payment_mode || null,
        admission_payment_modes: formData.admission_payment_modes || [],
        seat_booking_amount: formData.seat_booking_amount || 0,
        total_course_fee: formData.total_course_fee || 0,
        discount_amount: formData.discount_amount || 0,
        discount_id: formData.discount_id || null,
        scholarship_amount: formData.scholarship_amount || 0,
        scholarship_id: formData.scholarship_id || null,
        balance_fee: formData.balance_fee || 0,
        is_approved: editStudentId ? isApproved : false,
        is_blocked: editStudentId ? (formData.is_blocked || false) : false,
        is_lateral_entry: formData.is_lateral_entry || false,
        entry_semester: formData.entry_semester || null,
        ...(Object.keys(uploadedDocuments).length > 0 ? { documents: uploadedDocuments } : {})
      };
      
      // CRITICAL DEBUG LOGGING - Track exactly what user entered vs what's being sent
      console.log('============ FORM SUBMISSION DEBUG ============');
      console.log('📋 RAW FORM DATA BEFORE TRANSFORMATION:');
      console.log('  Father Name:', formData.father_name);
      console.log('  Mother Name:', formData.mother_name);
      console.log('  Guardian Name:', formData.guardian_name);
      console.log('  Guardian Relation:', formData.guardian_relation);
      console.log('  Class 10 Year:', formData.class_10_year);
      console.log('  Class 10 %:', formData.class_10_percentage);
      console.log('  Class 12 Year:', formData.class_12_year);
      console.log('  Class 12 %:', formData.class_12_percentage);
      console.log('\n📦 SUBMISSION DATA BEING SENT TO API:');
      console.log('  Father Name:', submissionData.father_name);
      console.log('  Mother Name:', submissionData.mother_name);
      console.log('  Guardian Name:', submissionData.guardian_name);
      console.log('  Guardian Relation:', submissionData.guardian_relation);
      console.log('  Class 10 Year:', submissionData.class_10_year);
      console.log('  Class 10 %:', submissionData.class_10_percentage);
      console.log('  Class 12 Year:', submissionData.class_12_year);
      console.log('  Class 12 %:', submissionData.class_12_percentage);
      console.log('📄 Documents:', Object.keys(uploadedDocuments));
      console.log('============================================');
      
      let response;
      if (editStudentId) {
        // Update existing student
        response = await api.put(`/students/${editStudentId}`, submissionData);
        console.log('Student admission updated:', response.data);
      } else {
        // Create new student admission
        response = await api.post('/students/', submissionData);
        console.log('Student admission created:', response.data);
      }
      
      // Get course name for success modal
      const courseName = courses.find(c => c.id === formData.course_id)?.name || formData.course_id;
      const studentName = `${formData.first_name} ${formData.middle_name || ''} ${formData.last_name}`.trim();
      const admissionNumber = response.data.admission_number || response.data.id;
      
      // If this was a booking conversion, mark booking as converted
      if (bookingId && bookingData) {
        try {
          // Update booking status to Converted
          await api.put(`/seat-booking/${bookingId}`, {
            status: 'Converted',
            converted_to_admission: true,
            admission_number: admissionNumber
          });
          console.log('Booking marked as converted');
        } catch (conversionError) {
          console.error('Error marking booking as converted:', conversionError);
          // Continue anyway since admission was created
        }
      }
      
      // Show success modal instead of alert
      if (editStudentId) {
        alert('Student details updated successfully!');
        navigate('/students/list');
      } else {
        // Show success modal for new admissions
        setSuccessData({
          studentName: studentName,
          admissionNumber: admissionNumber,
          courseName: courseName,
          studentId: response.data.id
        });
        setShowSuccessModal(true);
      }
    } catch (error) {
      console.error('Error admitting student:', error);
      alert(error.response?.data?.detail || 'Failed to admit student');
    } finally {
      setLoading(false);
    }
  };

  // Success Modal Component
  const SuccessModal = () => {
    if (!showSuccessModal) return null;
    
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 text-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle className="w-10 h-10 text-green-500" />
            </div>
            <h2 className="text-2xl font-bold text-white">Admission Successful!</h2>
            <p className="text-green-100 mt-1">Student has been admitted successfully</p>
          </div>
          
          {/* Content */}
          <div className="p-6 space-y-4">
            <div className="bg-gray-50 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Student Name</span>
                <span className="font-semibold text-gray-800">{successData.studentName}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Admission No</span>
                <span className="font-semibold text-green-600">{successData.admissionNumber}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-500 text-sm">Course</span>
                <span className="font-semibold text-gray-800">{successData.courseName}</span>
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                className="flex-1 flex items-center justify-center gap-2 py-6 border-2 hover:bg-gray-50"
                onClick={() => navigate(`/students/${successData.studentId}`)}
              >
                <Eye size={20} />
                View Student
              </Button>
              <Button
                className="flex-1 flex items-center justify-center gap-2 py-6 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700"
                onClick={() => navigate('/admission-hub')}
              >
                Continue
                <ArrowRight size={20} />
              </Button>
            </div>
            
            <p className="text-center text-xs text-gray-400 mt-2">
              Continue to process more pending admissions
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Success Modal */}
      <SuccessModal />
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              onClick={() => navigate(bookingId ? '/seat-booking/list' : '/students/list')}
              className="flex items-center gap-2"
            >
              <ArrowLeft size={20} />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <UserPlus className="text-blue-600" size={32} />
              {bookingId ? 'Convert Booking to Admission' : 'Student Admission'}
            </h1>
          </div>
          <p className="text-gray-600 mt-1 ml-14">
            {bookingId ? 'Complete student admission from seat booking' : 'Add new regular entry student admission (1st Semester)'}
          </p>
          {bookingData && (
            <div className="mt-2 ml-14 p-3 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-900 font-medium">
                <strong>Converting Booking:</strong> {bookingData.student_name} - {bookingData.email || bookingData.phone}
              </p>
              <p className="text-xs text-green-700 mt-1">
                <strong>Course:</strong> {bookingData.course_name || courses.find(c => c.id === bookingData.course_id)?.name || 'N/A'} 
                {bookingData.is_lateral_entry && <span className="ml-2 px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">Lateral Entry</span>}
              </p>
              <p className="text-xs text-green-700 mt-1">
                <strong>Booking Date:</strong> {bookingData.booking_date} | <strong>Session:</strong> {bookingData.session || 'N/A'} | <strong>Booking Amount:</strong> ₹{bookingData.booking_amount?.toLocaleString('en-IN')}
              </p>
              <p className="text-xs text-green-600 mt-1">
                ℹ️ Some fields have been pre-filled from the booking. Please complete the remaining required fields.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Admission Form */}
      <form onSubmit={handleSubmit} autoComplete="off" className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">

        {/* Read-Only Warning for Approved Students - OUTSIDE fieldset so button works */}
        {editStudentId && isApproved && (
          <div className="bg-yellow-50 border-2 border-yellow-400 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="text-2xl">🔒</span>
                <div>
                  <h3 className="text-lg font-bold text-yellow-800">View Only Mode</h3>
                  <p className="text-sm text-yellow-700">
                    This student's admission has been approved. Click "Edit Student" to make changes.
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsApproved(false)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-sm"
                data-testid="unlock-edit-btn"
              >
                Edit Student
              </button>
            </div>
          </div>
        )}

        <fieldset disabled={editStudentId && isApproved} className={editStudentId && isApproved ? 'opacity-70' : ''}>
        <div className="space-y-6">
          
          {/* Edit Mode Info for Non-Approved Students */}
          {editStudentId && !isApproved && (
            <div className="bg-blue-50 border-2 border-blue-400 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <span className="text-2xl">✏️</span>
                <div>
                  <h3 className="text-lg font-bold text-blue-800">Edit Mode</h3>
                  <p className="text-sm text-blue-700">
                    You can edit this student's information. Changes will be saved when you click "Update Student".
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Student ID Preview Box */}
          {!editStudentId && (
            <div className={`border-2 rounded-lg p-4 ${!collegeInfo.college_code ? 'bg-red-50 border-red-300' : 'bg-green-50 border-green-300'}`}>
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <h3 className="text-sm font-semibold text-gray-800 mb-1">
                    📋 Student ID Preview
                  </h3>
                  {!collegeInfo.college_code ? (
                    <div>
                      <p className="text-sm text-red-700 font-medium">
                        ⚠️ College Code not set! Student ID will show as: <code className="bg-red-100 px-2 py-0.5 rounded">XXX.{formData.course_id ? (courses.find(c => c.id === formData.course_id)?.code || 'COURSE') : 'COURSE'}{formData.is_lateral_entry ? 'L' : ''}{formData.session?.slice(0,4) || '2025'}001</code>
                      </p>
                      <p className="text-xs text-red-600 mt-1">
                        Go to <strong>Settings → College Settings</strong> to set your College Code first.
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-lg font-mono font-bold text-green-800">
                        {collegeInfo.college_code}.{formData.course_id ? (courses.find(c => c.id === formData.course_id)?.code || 'COURSE') : 'COURSE'}{formData.is_lateral_entry ? 'L' : ''}{formData.session?.slice(0,4) || '2025'}XXX
                      </p>
                      <p className="text-xs text-green-600 mt-1">
                        Format: {collegeInfo.college_code} (College) + CourseCode + {formData.is_lateral_entry ? 'L (Lateral) + ' : ''}Year + Serial Number
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Basic Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Basic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Student ID <span className="text-xs text-green-600">(Auto-generated on save)</span>
                </label>
                <input
                  type="text"
                  name="admission_no"
                  value={formData.admission_no || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-100"
                  readOnly
                  placeholder={formData.is_lateral_entry ? "e.g. ILOEBBEADL2025001 (Auto-generated)" : "e.g. ILOEBBED2025001 (Auto-generated)"}
                />
                <p className="text-xs text-blue-600 mt-1">
                  {formData.is_lateral_entry 
                    ? "Format: CollegeCode.CourseCode+L+Year+Serial (Lateral Entry)" 
                    : "Format: CollegeCode.CourseCode+Year+Serial (Regular Entry)"}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  College Roll Number <span className="text-xs text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="roll_number"
                  value={formData.roll_number || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. 2025/CS/001"
                  data-testid="student-roll-number-input"
                />
                <p className="text-xs text-gray-500 mt-1">College-specific roll number. Must be unique within this college if provided.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  University ID <span className="text-xs text-gray-500">(Optional)</span>
                </label>
                <input
                  type="text"
                  name="university_id"
                  value={formData.university_id || ''}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g. WBUT/24/CSE/001"
                  data-testid="student-university-id-input"
                />
                <p className="text-xs text-gray-500 mt-1">University-issued registration/enrollment ID. Must be unique if provided.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Middle Name
                </label>
                <input
                  type="text"
                  name="middle_name"
                  value={formData.middle_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone (Primary) *
                </label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  WhatsApp No. *
                </label>
                <input
                  type="tel"
                  name="whatsapp_no"
                  value={formData.whatsapp_no}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date of Birth *
                </label>
                <input
                  type="date"
                  name="date_of_birth"
                  value={formData.date_of_birth}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Gender *
                </label>
                <select
                  name="gender"
                  value={formData.gender}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Blood Group
                </label>
                <select
                  name="blood_group"
                  value={formData.blood_group}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Blood Group</option>
                  <option value="A+">A+</option>
                  <option value="A-">A-</option>
                  <option value="B+">B+</option>
                  <option value="B-">B-</option>
                  <option value="AB+">AB+</option>
                  <option value="AB-">AB-</option>
                  <option value="O+">O+</option>
                  <option value="O-">O-</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Religion
                </label>
                <select
                  name="religion"
                  value={formData.religion}
                  onChange={handleChange}
                  onFocus={ensureReligionsLoaded}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Religion</option>
                  {religions.length > 0 ? (
                    religions.map(rel => (
                      <option key={rel.id} value={rel.name}>{rel.name}</option>
                    ))
                  ) : (
                    <>
                      <option value="Hindu">Hindu</option>
                      <option value="Muslim">Muslim</option>
                      <option value="Christian">Christian</option>
                      <option value="Sikh">Sikh</option>
                      <option value="Buddhist">Buddhist</option>
                      <option value="Jain">Jain</option>
                      <option value="Other">Other</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Caste Category
                </label>
                <select
                  name="caste_category"
                  value={formData.caste_category}
                  onChange={handleChange}
                  onFocus={ensureCastesLoaded}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Category</option>
                  {castes.length > 0 ? (
                    castes.map(caste => (
                      <option key={caste.id} value={caste.name}>{caste.name}</option>
                    ))
                  ) : (
                    <>
                      <option value="General">General</option>
                      <option value="OBC">OBC</option>
                      <option value="SC">SC</option>
                      <option value="ST">ST</option>
                      <option value="EWS">EWS</option>
                    </>
                  )}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Aadhar No.
                </label>
                <input
                  type="text"
                  name="aadhar_no"
                  value={formData.aadhar_no}
                  onChange={handleChange}
                  maxLength="12"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="12 digit Aadhar"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nationality
                </label>
                <input
                  type="text"
                  name="nationality"
                  value={formData.nationality}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Medium of Instruction
                </label>
                <select
                  name="medium_of_instruction"
                  value={formData.medium_of_instruction}
                  onChange={handleChange}
                  onFocus={ensureLanguagesLoaded}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Medium</option>
                  {languages.map(lang => (
                    <option key={lang.id} value={lang.name}>{lang.name} Medium</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Academic Details - 10th & 12th (Required for ALL students) */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">10th Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                    <input
                      type="text"
                      name="class_10_school"
                      value={formData.class_10_school}
                      onChange={handleChange}
                      placeholder="e.g., ABC High School"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Board</label>
                    <input
                      type="text"
                      name="class_10_board"
                      value={formData.class_10_board}
                      onChange={handleChange}
                      placeholder="e.g., CBSE"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passing Year</label>
                    <input
                      type="text"
                      name="class_10_year"
                      value={formData.class_10_year}
                      onChange={handleChange}
                      placeholder="e.g., 2020"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage</label>
                    <input
                      type="number"
                      step="0.01"
                      name="class_10_percentage"
                      value={formData.class_10_percentage}
                      onChange={handleChange}
                      placeholder="e.g., 85.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">10+2 Details</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Name</label>
                    <input
                      type="text"
                      name="class_12_school"
                      value={formData.class_12_school}
                      onChange={handleChange}
                      placeholder="e.g., XYZ Higher Secondary School"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Board</label>
                    <input
                      type="text"
                      name="class_12_board"
                      value={formData.class_12_board}
                      onChange={handleChange}
                      placeholder="e.g., CBSE"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passing Year</label>
                    <input
                      type="text"
                      name="class_12_year"
                      value={formData.class_12_year}
                      onChange={handleChange}
                      placeholder="e.g., 2022"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage</label>
                    <input
                      type="number"
                      step="0.01"
                      name="class_12_percentage"
                      value={formData.class_12_percentage}
                      onChange={handleChange}
                      placeholder="e.g., 78.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

          {/* Lateral Entry - Additional Diploma Details */}
          {formData.is_lateral_entry && (
            <>
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4">Diploma Details *</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diploma Name *</label>
                    <input
                      type="text"
                      name="diploma_name"
                      value={formData.diploma_name}
                      onChange={handleChange}
                      placeholder="e.g., Diploma in Pharmacy"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={formData.is_lateral_entry}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">College Name *</label>
                    <input
                      type="text"
                      name="diploma_college"
                      value={formData.diploma_college}
                      onChange={handleChange}
                      placeholder="e.g., ABC Polytechnic"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={formData.is_lateral_entry}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Board/University *</label>
                    <input
                      type="text"
                      name="diploma_board"
                      value={formData.diploma_board}
                      onChange={handleChange}
                      placeholder="e.g., State Board"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={formData.is_lateral_entry}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Passing Year *</label>
                    <input
                      type="text"
                      name="diploma_year"
                      value={formData.diploma_year}
                      onChange={handleChange}
                      placeholder="e.g., 2023"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={formData.is_lateral_entry}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Percentage *</label>
                    <input
                      type="number"
                      step="0.01"
                      name="diploma_percentage"
                      value={formData.diploma_percentage}
                      onChange={handleChange}
                      placeholder="e.g., 75.5"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={formData.is_lateral_entry}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Diploma Certificate</label>
                    <input
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => handleDocumentUpload(e, 'diploma_certificate')}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gap Year (if any)</label>
                    <input
                      type="text"
                      name="gap_year"
                      value={formData.gap_year}
                      onChange={handleChange}
                      placeholder="e.g., 2024"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Gap Reason</label>
                    <input
                      type="text"
                      name="gap_reason"
                      value={formData.gap_reason}
                      onChange={handleChange}
                      placeholder="Reason for gap"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Optional Higher Education Sections */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Higher Education (Optional)</h2>
            <div className="space-y-4">
              {/* Graduation Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="has_graduation"
                  checked={formData.has_graduation}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      has_graduation: e.target.checked,
                      // Clear graduation fields if unchecked
                      ...(!e.target.checked && {
                        graduation_degree: '',
                        graduation_college: '',
                        graduation_university: '',
                        graduation_year: '',
                        graduation_percentage: ''
                      })
                    }));
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="has_graduation" className="ml-2 text-sm font-medium text-gray-700">
                  Add Graduation Details
                </label>
              </div>

              {/* Graduation Section */}
              {formData.has_graduation && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-md font-semibold text-gray-700 mb-3">Graduation Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Degree Name</label>
                      <input
                        type="text"
                        name="graduation_degree"
                        value={formData.graduation_degree}
                        onChange={handleChange}
                        placeholder="e.g., B.Sc Computer Science"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">College Name</label>
                      <input
                        type="text"
                        name="graduation_college"
                        value={formData.graduation_college}
                        onChange={handleChange}
                        placeholder="College name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">University/Board</label>
                      <input
                        type="text"
                        name="graduation_university"
                        value={formData.graduation_university}
                        onChange={handleChange}
                        placeholder="University name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Passing Year</label>
                      <input
                        type="text"
                        name="graduation_year"
                        value={formData.graduation_year}
                        onChange={handleChange}
                        placeholder="e.g., 2023"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Percentage/CGPA</label>
                      <input
                        type="text"
                        name="graduation_percentage"
                        value={formData.graduation_percentage}
                        onChange={handleChange}
                        placeholder="e.g., 75.5% or 8.5 CGPA"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Graduation Certificate</label>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload(e, 'graduation_certificate')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Masters Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="has_masters"
                  checked={formData.has_masters}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      has_masters: e.target.checked,
                      // Clear masters fields if unchecked
                      ...(!e.target.checked && {
                        masters_degree: '',
                        masters_college: '',
                        masters_university: '',
                        masters_year: '',
                        masters_percentage: ''
                      })
                    }));
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="has_masters" className="ml-2 text-sm font-medium text-gray-700">
                  Add Masters/Postgraduate Details
                </label>
              </div>

              {/* Masters Section */}
              {formData.has_masters && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-md font-semibold text-gray-700 mb-3">Masters/Postgraduate Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Degree Name</label>
                      <input
                        type="text"
                        name="masters_degree"
                        value={formData.masters_degree}
                        onChange={handleChange}
                        placeholder="e.g., M.Sc Computer Science"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">College Name</label>
                      <input
                        type="text"
                        name="masters_college"
                        value={formData.masters_college}
                        onChange={handleChange}
                        placeholder="College name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">University/Board</label>
                      <input
                        type="text"
                        name="masters_university"
                        value={formData.masters_university}
                        onChange={handleChange}
                        placeholder="University name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Passing Year</label>
                      <input
                        type="text"
                        name="masters_year"
                        value={formData.masters_year}
                        onChange={handleChange}
                        placeholder="e.g., 2025"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Percentage/CGPA</label>
                      <input
                        type="text"
                        name="masters_percentage"
                        value={formData.masters_percentage}
                        onChange={handleChange}
                        placeholder="e.g., 80.5% or 9.0 CGPA"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Masters Certificate</label>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload(e, 'masters_certificate')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* PhD Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="has_phd"
                  checked={formData.has_phd}
                  onChange={(e) => {
                    setFormData(prev => ({
                      ...prev,
                      has_phd: e.target.checked,
                      // Clear PhD fields if unchecked
                      ...(!e.target.checked && {
                        phd_title: '',
                        phd_university: '',
                        phd_year: '',
                        phd_status: ''
                      })
                    }));
                  }}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="has_phd" className="ml-2 text-sm font-medium text-gray-700">
                  Add PhD/Doctoral Details
                </label>
              </div>

              {/* PhD Section */}
              {formData.has_phd && (
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <h3 className="text-md font-semibold text-gray-700 mb-3">PhD/Doctoral Details</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Research Title</label>
                      <input
                        type="text"
                        name="phd_title"
                        value={formData.phd_title}
                        onChange={handleChange}
                        placeholder="Research topic/title"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">University</label>
                      <input
                        type="text"
                        name="phd_university"
                        value={formData.phd_university}
                        onChange={handleChange}
                        placeholder="University name"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Year</label>
                      <input
                        type="text"
                        name="phd_year"
                        value={formData.phd_year}
                        onChange={handleChange}
                        placeholder="e.g., 2027"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                      <select
                        name="phd_status"
                        value={formData.phd_status}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        <option value="">Select Status</option>
                        <option value="Pursuing">Pursuing</option>
                        <option value="Completed">Completed</option>
                        <option value="Submitted">Submitted</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">PhD Certificate</label>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleDocumentUpload(e, 'phd_certificate')}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Guardian Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Father's Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Father's Name</label>
                <input
                  type="text"
                  name="father_name"
                  value={formData.father_name}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Father's Phone</label>
                <input
                  type="tel"
                  name="father_phone"
                  value={formData.father_phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Father's Email</label>
                <input
                  type="email"
                  name="father_email"
                  value={formData.father_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Father's Occupation</label>
                <select
                  name="father_occupation"
                  value={formData.father_occupation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Occupation</option>
                  <option value="Government Service">Government Service</option>
                  <option value="Private Service">Private Service</option>
                  <option value="Business">Business</option>
                  <option value="Self-Employed">Self-Employed</option>
                  <option value="Farmer/Agriculture">Farmer/Agriculture</option>
                  <option value="Teacher/Professor">Teacher/Professor</option>
                  <option value="Doctor/Medical">Doctor/Medical</option>
                  <option value="Engineer">Engineer</option>
                  <option value="Lawyer/Advocate">Lawyer/Advocate</option>
                  <option value="Bank Employee">Bank Employee</option>
                  <option value="Police/Defence">Police/Defence</option>
                  <option value="Daily Wage Worker">Daily Wage Worker</option>
                  <option value="Retired">Retired</option>
                  <option value="Not Employed">Not Employed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Mother's Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Name</label>
                <input
                  type="text"
                  name="mother_name"
                  value={formData.mother_name}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Phone</label>
                <input
                  type="tel"
                  name="mother_phone"
                  value={formData.mother_phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Email</label>
                <input
                  type="email"
                  name="mother_email"
                  value={formData.mother_email}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Mother's Occupation</label>
                <select
                  name="mother_occupation"
                  value={formData.mother_occupation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select Occupation</option>
                  <option value="Homemaker">Homemaker</option>
                  <option value="Government Service">Government Service</option>
                  <option value="Private Service">Private Service</option>
                  <option value="Business">Business</option>
                  <option value="Self-Employed">Self-Employed</option>
                  <option value="Teacher/Professor">Teacher/Professor</option>
                  <option value="Doctor/Medical">Doctor/Medical</option>
                  <option value="Engineer">Engineer</option>
                  <option value="Lawyer/Advocate">Lawyer/Advocate</option>
                  <option value="Bank Employee">Bank Employee</option>
                  <option value="Police/Defence">Police/Defence</option>
                  <option value="Daily Wage Worker">Daily Wage Worker</option>
                  <option value="Retired">Retired</option>
                  <option value="Not Employed">Not Employed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>
          </div>

          {/* Permanent Address */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Permanent Address</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address/C/o</label>
                <input
                  type="text"
                  name="permanent_address"
                  value={formData.permanent_address}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="C/o or Address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City/Village</label>
                <input
                  type="text"
                  name="permanent_city"
                  value={formData.permanent_city}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  name="permanent_state"
                  value={formData.permanent_state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <select
                  name="permanent_district"
                  value={formData.permanent_district}
                  onChange={handleChange}
                  disabled={!formData.permanent_state}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select District</option>
                  {formData.permanent_state && INDIAN_STATES_DISTRICTS[formData.permanent_state]?.map((district) => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                <input
                  type="text"
                  name="permanent_pin_code"
                  value={formData.permanent_pin_code}
                  onChange={handleChange}
                  maxLength="6"
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Landmark</label>
                <input
                  type="text"
                  name="permanent_landmark"
                  value={formData.permanent_landmark}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Current Address */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-800">Current Address</h2>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sameAsPermanent}
                  onChange={handleSameAsPermanent}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Same as Permanent Address</span>
              </label>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Address/C/o</label>
                <input
                  type="text"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="C/o or Address"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">City/Village</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                <select
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select State</option>
                  {INDIAN_STATES.map((state) => (
                    <option key={state} value={state}>{state}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
                <select
                  name="district"
                  value={formData.district}
                  onChange={handleChange}
                  disabled={!formData.state}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                  <option value="">Select District</option>
                  {formData.state && INDIAN_STATES_DISTRICTS[formData.state]?.map((district) => (
                    <option key={district} value={district}>{district}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                <input
                  type="text"
                  name="pin_code"
                  value={formData.pin_code}
                  onChange={handleChange}
                  maxLength="6"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Landmark</label>
                <input
                  type="text"
                  name="landmark"
                  value={formData.landmark}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Guardian Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Guardian Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guardian Name *
                </label>
                <input
                  type="text"
                  name="guardian_name"
                  value={formData.guardian_name}
                  onChange={handleChange}
                  autoComplete="off"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Guardian Phone *
                </label>
                <input
                  type="tel"
                  name="guardian_phone"
                  value={formData.guardian_phone}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Relation *
                </label>
                <select
                  name="guardian_relation"
                  value={formData.guardian_relation}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="Father">Father</option>
                  <option value="Mother">Mother</option>
                  <option value="Guardian">Guardian</option>
                </select>
              </div>
            </div>
          </div>

          {/* Academic Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Academic Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course *
                </label>
                <select
                  name="course_id"
                  value={formData.course_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Course</option>
                  {courses.map(course => (
                    <option key={course.id} value={course.id}>{course.name}</option>
                  ))}
                </select>
              </div>
              <MethodSubjectField
                courseId={formData.course_id}
                courses={courses}
                value={formData.method_subject}
                onChange={(val) => setFormData(prev => ({ ...prev, method_subject: val }))}
              />
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Session * {!formData.course_id && <span className="text-xs text-gray-500">(Select course first)</span>}
                </label>
                <select
                  name="session"
                  value={formData.session}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={!formData.course_id}
                >
                  <option value="">Select Session</option>
                  {(() => {
                    // Filter sessions for current course; show appropriate ones for entry type.
                    if (!formData.course_id) return null;
                    const courseSessions = filterSessionsByCourse(sessions, formData.course_id);
                    // If any session is explicitly tagged "Lateral", treat course as
                    // having distinct lateral-only sessions; otherwise treat all as shared.
                    const hasLateralSpecific = courseSessions.some(s => s.entry_type === 'Lateral');
                    const visible = courseSessions.filter(s => {
                      if (formData.is_lateral_entry) {
                        // Lateral mode: prefer Lateral-tagged; if none exist, show all.
                        if (hasLateralSpecific) return s.entry_type === 'Lateral';
                        return true; // shared sessions
                      }
                      // Regular mode: hide sessions explicitly tagged Lateral.
                      return s.entry_type !== 'Lateral';
                    });
                    return visible.map(session => (
                      <option
                        key={session.id}
                        value={session.name}
                        className={session.is_current ? 'font-bold' : ''}
                      >
                        {session.is_current ? `✓ ${session.name} (Current)` : session.name}
                      </option>
                    ));
                  })()}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Semester * <span className="text-xs text-blue-600">(Auto: {formData.is_lateral_entry ? '3rd Sem' : '1st Sem'})</span>
                </label>
                <select
                  name="semester_id"
                  value={formData.semester_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  disabled={!formData.course_id}
                >
                  <option value="">Select Semester</option>
                  {semesters.map(semester => (
                    <option key={semester.id} value={semester.id}>{semester.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Section *
                </label>
                <select
                  name="section_id"
                  value={formData.section_id}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="">Select Section</option>
                  {sections.map(section => (
                    <option key={section.id} value={section.id}>{section.name}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Entry Type Selector - Only show if selected course supports lateral entry */}
            {selectedCourseData?.lateral_entry_allowed && (
              <div className="mt-4 bg-gradient-to-r from-amber-50 to-orange-50 border-2 border-amber-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <h3 className="text-base font-semibold text-gray-800">🎓 Entry Type</h3>
                    <p className="text-xs text-gray-600">This course supports Lateral Entry (Semester {selectedCourseData.lateral_entry_semester || 3})</p>
                  </div>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
                      <input
                        type="radio"
                        name="entry_type"
                        checked={!formData.is_lateral_entry}
                        onChange={() => handleEntryTypeChange(false)}
                        className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Regular Entry
                        <span className="block text-xs text-gray-500">From Semester 1</span>
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer bg-white px-3 py-2 rounded-lg border border-gray-200 hover:border-orange-300 transition-colors">
                      <input
                        type="radio"
                        name="entry_type"
                        checked={formData.is_lateral_entry}
                        onChange={() => handleEntryTypeChange(true)}
                        className="w-4 h-4 text-orange-600 border-gray-300 focus:ring-orange-500"
                      />
                      <span className="text-sm font-medium text-gray-700">
                        Lateral Entry
                        <span className="block text-xs text-gray-500">Diploma → Sem {selectedCourseData.lateral_entry_semester || 3}</span>
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admission Date *
                </label>
                <input
                  type="date"
                  name="admission_date"
                  data-testid="admission-date-input"
                  value={formData.admission_date}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>
            </div>
            {bookingData && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                ℹ️ <strong>Note:</strong> Session, Name, Email, Phone, Course, and Booking Amount have been auto-filled from the booking.
              </div>
            )}
            {onlineApplicationData && (
              <div className="mt-2 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-800">
                🌐 <strong>Online Application:</strong> This data was pre-filled from online application #{onlineApplicationData.application_number}. Please review and approve.
              </div>
            )}
          </div>

          {/* Upload Documents */}
          <div className="bg-green-50 p-6 rounded-lg border border-green-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">Upload Documents</h2>
            <p className="text-sm text-gray-600 mb-4">Upload documents as per configured limits</p>
            
            {documentSettingsLoading ? (
              <div className="flex items-center justify-center py-8">
                <span className="text-gray-600">Loading document requirements...</span>
              </div>
            ) : documentSettings.length > 0 ? (
              /* Dynamic Documents from Document Settings */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Filter out duplicate documents by normalized name and track matched docs */}
                {(() => {
                  const seenDocKeys = new Set();
                  const matchedOnlineDocKeys = new Set(); // Track which online docs have been matched
                  
                  const filteredDocs = documentSettings.filter(doc => {
                    const docKey = doc.document_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                    if (seenDocKeys.has(docKey)) {
                      return false; // Skip duplicate
                    }
                    seenDocKeys.add(docKey);
                    return true;
                  });
                  
                  return filteredDocs;
                })().map((doc) => {
                  // Generate key exactly like PublicAdmissionForm does
                  const docKey = doc.document_name.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                  
                  // Also generate alternative keys
                  const altDocKey1 = doc.document_name.toLowerCase().replace(/\s+/g, '_'); // Without removing special chars
                  const altDocKey2 = doc.document_name.toLowerCase().replace(/[^a-z0-9]/g, '_'); // Replace all non-alphanumeric
                  const altDocKey3 = 'class_' + docKey.replace(/^(\d+)(th|nd|rd|st)_/, ''); // class_10_marksheet style
                  
                  // Common document name mappings (Document Settings Name -> Online Form Key)
                  const documentMappings = {
                    'profile_photo': ['passport_photo', 'photo', 'profile_photo', 'student_photo'],
                    'passport_photo': ['passport_photo', 'profile_photo', 'photo', 'student_photo'],
                    'photo': ['passport_photo', 'profile_photo', 'photo', 'student_photo'],
                    // Marksheet mappings - include class_ prefix variants
                    '10th_marksheet': ['10th_marksheet', 'class_10_marksheet', 'ssc_marksheet', 'madhyamik_marksheet', 'class_10_result', '10thmarksheet'],
                    '10th_certificate': ['10th_certificate', 'class_10_certificate', 'ssc_certificate', 'madhyamik_certificate', '10thcertificate'],
                    '12th_marksheet': ['12th_marksheet', 'class_12_marksheet', 'hsc_marksheet', 'hs_marksheet', 'higher_secondary_marksheet', 'class_12_result', '12thmarksheet'],
                    '12th_certificate': ['12th_certificate', 'class_12_certificate', 'hsc_certificate', 'hs_certificate', 'higher_secondary_certificate', '12thcertificate'],
                    'aadhar_card': ['aadhar_card', 'aadhaar_card', 'aadhar', 'aadhaar', 'aadharcard'],
                    'caste_certificate': ['caste_certificate', 'cast_certificate', 'category_certificate', 'castecertificate'],
                    'income_certificate': ['income_certificate', 'income_proof', 'incomecertificate'],
                    'domicile_certificate': ['domicile_certificate', 'domicile', 'residential_certificate', 'domicilecertificate'],
                    'signature': ['signature', 'student_signature'],
                    'transfer_certificate': ['transfer_certificate', 'tc', 'transfercertificate'],
                    'migration_certificate': ['migration_certificate', 'mc', 'migrationcertificate'],
                    'graduation_marksheet': ['graduation_marksheet', 'ug_marksheet', 'degree_marksheet'],
                    'graduation_certificate': ['graduation_certificate', 'ug_certificate', 'degree_certificate'],
                    'other_certificates': ['other_certificates', 'other_documents', 'additional_documents', 'othercertificates'],
                  };
                  
                  // Try to find the document - check multiple possible keys
                  let existingDoc = null;
                  let matchedKey = null;
                  
                  // All possible keys to try
                  const allKeysToTry = [docKey, altDocKey1, altDocKey2, altDocKey3];
                  
                  // Add mappings for each key
                  for (const key of [docKey, altDocKey1]) {
                    if (documentMappings[key]) {
                      allKeysToTry.push(...documentMappings[key]);
                    }
                  }
                  
                  // Also add normalized versions (no underscores)
                  allKeysToTry.push(docKey.replace(/_/g, ''));
                  
                  // Remove duplicates
                  const uniqueKeys = [...new Set(allKeysToTry)];
                  
                  // Try each key
                  for (const keyToTry of uniqueKeys) {
                    if (documents[keyToTry]) {
                      existingDoc = documents[keyToTry];
                      matchedKey = keyToTry;
                      break;
                    }
                  }
                  
                  // If still not found, try fuzzy match on all document keys
                  if (!existingDoc) {
                    const allDocKeys = Object.keys(documents);
                    const normalizedDocKey = docKey.replace(/[^a-z0-9]/g, '');
                    
                    for (const dKey of allDocKeys) {
                      const normalizedDKey = dKey.replace(/[^a-z0-9]/g, '');
                      // Check if they match when normalized
                      if (normalizedDocKey === normalizedDKey || 
                          normalizedDocKey.includes(normalizedDKey) || 
                          normalizedDKey.includes(normalizedDocKey)) {
                        existingDoc = documents[dKey];
                        matchedKey = dKey;
                        break;
                      }
                    }
                  }
                  
                  const acceptFormats = doc.allowed_formats?.map(f => {
                    if (f === 'pdf') return '.pdf';
                    if (f === 'jpg' || f === 'jpeg') return '.jpg,.jpeg';
                    if (f === 'png') return '.png';
                    return `image/${f}`;
                  }).join(',') || 'image/*,.pdf';
                  
                  // Check if document exists (from online application - base64 string or URL)
                  const isBase64 = typeof existingDoc === 'string' && existingDoc.startsWith('data:');
                  const isUrl = typeof existingDoc === 'string' && (existingDoc.startsWith('http://') || existingDoc.startsWith('https://'));
                  const isFile = existingDoc && existingDoc instanceof File;
                  const hasExistingDoc = isBase64 || isUrl;
                  const isImage = isBase64 ? existingDoc.startsWith('data:image') : (isUrl && (existingDoc.includes('.jpg') || existingDoc.includes('.png') || existingDoc.includes('.jpeg')));
                  
                  return (
                    <div key={doc.id} className="bg-white p-3 rounded-lg border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {doc.document_name} {doc.is_mandatory && <span className="text-red-500">*</span>}
                      </label>
                      <p className="text-xs text-gray-400 mb-2">
                        Max: {doc.max_size_kb || 300}KB | {doc.allowed_formats?.join(', ').toUpperCase() || 'PDF, JPG, PNG'}
                      </p>
                      {doc.description && (
                        <p className="text-xs text-gray-500 mb-1">{doc.description}</p>
                      )}
                      
                      {/* Show existing document from Online Application */}
                      {hasExistingDoc && (
                        <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            {/* Only show preview for images (JPG/PNG/JPEG) */}
                            {isImage && (
                              <img 
                                src={existingDoc} 
                                alt={doc.document_name}
                                className="w-16 h-16 object-cover rounded border"
                              />
                            )}
                            <div className="flex-1">
                              <p className="text-xs text-green-700 font-medium">✓ From Online Form</p>
                              <p className="text-xs text-gray-500">{isImage ? 'Image' : 'PDF'} file</p>
                              <div className="flex gap-1 mt-1">
                                <a 
                                  href={existingDoc} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                >
                                  View
                                </a>
                                <a 
                                  href={existingDoc} 
                                  download={`${docKey}.${isImage ? 'jpg' : 'pdf'}`}
                                  className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(docKey)}
                            className="text-xs text-red-600 hover:text-red-800 underline"
                          >
                            Remove & Upload New
                          </button>
                        </div>
                      )}
                      
                      {/* Show upload option if no existing doc or user wants to replace */}
                      {!hasExistingDoc && (
                        <>
                          <div className="relative">
                            <input
                              type="file"
                              accept={acceptFormats}
                              onChange={(e) => handleFileChange(docKey, e.target.files[0], doc)}
                              className="hidden"
                              id={`doc-${docKey}`}
                            />
                            <label
                              htmlFor={`doc-${docKey}`}
                              className={`flex items-center justify-center w-full px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50 ${
                                doc.is_mandatory ? 'border-red-300 bg-red-50' : 'border-gray-300 bg-white'
                              }`}
                            >
                              <Upload size={16} className="mr-2" />
                              <span className="text-sm">Choose File</span>
                            </label>
                            {isFile && (
                              <div className="mt-1 text-xs text-green-600 flex items-center justify-between">
                                <span className="truncate">{existingDoc.name}</span>
                                <button
                                  type="button"
                                  onClick={() => handleRemoveFile(docKey)}
                                  className="ml-2 text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Fallback - Show all documents from online application + default fields */
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {(() => {
                  // Mapping of alternative keys for the same document (to avoid duplicates)
                  const keyMappings = {
                    'passport_photo': ['passport_photo', 'profile_photo', 'photo'],
                    'aadhar_card': ['aadhar_card', 'aadhaar_card', 'aadhar'],
                    '10th_marksheet': ['10th_marksheet', 'class_10_marksheet', 'ssc_marksheet'],
                    '10th_certificate': ['10th_certificate', 'class_10_certificate'],
                    '12th_marksheet': ['12th_marksheet', 'class_12_marksheet', 'hsc_marksheet'],
                    '12th_certificate': ['12th_certificate', 'class_12_certificate'],
                    'caste_certificate': ['caste_certificate', 'cast_certificate'],
                    'income_certificate': ['income_certificate', 'income_proof'],
                    'domicile_certificate': ['domicile_certificate', 'domicile'],
                  };
                  
                  // Helper function to find document by primary key or any of its alternatives
                  const findDocumentByKey = (primaryKey) => {
                    const altKeys = keyMappings[primaryKey] || [primaryKey];
                    for (const key of altKeys) {
                      if (documents[key]) return documents[key];
                    }
                    return null;
                  };
                  
                  // Default document configs - MUST match Document Settings defaults (9 docs only)
                  const defaultDocs = [
                    { key: 'passport_photo', label: 'Passport Photo', required: true, accept: 'image/*' },
                    { key: '10th_marksheet', label: '10th Marksheet', required: true, accept: 'image/*,.pdf' },
                    { key: '10th_certificate', label: '10th Certificate', required: true, accept: 'image/*,.pdf' },
                    { key: '12th_marksheet', label: '12th Marksheet', required: true, accept: 'image/*,.pdf' },
                    { key: '12th_certificate', label: '12th Certificate', required: true, accept: 'image/*,.pdf' },
                    { key: 'aadhar_card', label: 'Aadhar Card', required: true, accept: 'image/*,.pdf' },
                    { key: 'caste_certificate', label: 'Caste Certificate', required: false, accept: 'image/*,.pdf' },
                    { key: 'income_certificate', label: 'Income Certificate', required: false, accept: 'image/*,.pdf' },
                    { key: 'domicile_certificate', label: 'Domicile Certificate', required: false, accept: 'image/*,.pdf' }
                  ];
                  
                  // Track all handled keys (including alternatives) to avoid duplicates
                  const handledKeys = new Set();
                  defaultDocs.forEach(d => {
                    handledKeys.add(d.key);
                    const altKeys = keyMappings[d.key] || [];
                    altKeys.forEach(k => handledKeys.add(k));
                  });
                  
                  // Add any extra documents from online form that aren't in defaultDocs
                  Object.keys(documents).forEach(key => {
                    if (!handledKeys.has(key) && documents[key]) {
                      const label = key.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                      defaultDocs.push({ key, label, required: false, accept: 'image/*,.pdf' });
                      handledKeys.add(key);
                    }
                  });
                  
                  return defaultDocs.map((docConfig) => {
                    const existingDoc = findDocumentByKey(docConfig.key);
                  const isBase64 = typeof existingDoc === 'string' && existingDoc.startsWith('data:');
                  const isUrl = typeof existingDoc === 'string' && (existingDoc.startsWith('http://') || existingDoc.startsWith('https://'));
                  const isFile = existingDoc && existingDoc instanceof File;
                  const hasExistingDoc = isBase64 || isUrl;
                  const isImage = isBase64 ? existingDoc.startsWith('data:image') : (isUrl && (existingDoc.includes('.jpg') || existingDoc.includes('.png') || existingDoc.includes('.jpeg')));
                  
                  return (
                    <div key={docConfig.key} className="bg-white p-3 rounded-lg border border-gray-200">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {docConfig.label} {docConfig.required && <span className="text-red-500">*</span>}
                      </label>
                      
                      {/* Show existing document from Online Application */}
                      {hasExistingDoc && (
                        <div className="mb-2 p-2 bg-green-50 border border-green-200 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            {/* Only show preview for images (JPG/PNG/JPEG) */}
                            {isImage && (
                              <img 
                                src={existingDoc} 
                                alt={docConfig.label}
                                className="w-16 h-16 object-cover rounded border"
                              />
                            )}
                            <div className="flex-1">
                              <p className="text-xs text-green-700 font-medium">✓ From Online Form</p>
                              <p className="text-xs text-gray-500">{isImage ? 'Image' : 'PDF'} file</p>
                              <div className="flex gap-1 mt-1">
                                <a 
                                  href={existingDoc} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  className="text-xs bg-blue-500 text-white px-2 py-1 rounded hover:bg-blue-600"
                                >
                                  View
                                </a>
                                <a 
                                  href={existingDoc} 
                                  download={`${docConfig.key}.${isImage ? 'jpg' : 'pdf'}`}
                                  className="text-xs bg-gray-500 text-white px-2 py-1 rounded hover:bg-gray-600"
                                >
                                  Download
                                </a>
                              </div>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveFile(docConfig.key)}
                            className="text-xs text-red-600 hover:text-red-800 underline"
                          >
                            Remove & Upload New
                          </button>
                        </div>
                      )}
                      
                      {/* Show upload option if no existing doc */}
                      {!hasExistingDoc && (
                        <div className="relative">
                          <input
                            type="file"
                            accept={docConfig.accept}
                            onChange={(e) => handleFileChange(docConfig.key, e.target.files[0], { max_size_kb: 300 })}
                            className="hidden"
                            id={docConfig.key}
                          />
                          <label
                            htmlFor={docConfig.key}
                            className="flex items-center justify-center w-full px-4 py-2 border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 bg-white"
                          >
                            <Upload size={16} className="mr-2" />
                            <span className="text-sm">Choose File</span>
                          </label>
                          {isFile && (
                            <div className="mt-1 text-xs text-green-600 flex items-center justify-between">
                              <span className="truncate">{existingDoc.name}</span>
                              <button
                                type="button"
                                onClick={() => handleRemoveFile(docConfig.key)}
                                className="ml-2 text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                  });
                })()}
              </div>
            )}
          </div>

          {/* Consultant & Fee Information */}
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-lg border-2 border-blue-200">
            <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
              <DollarSign className="h-5 w-5 text-blue-600" />
              Consultant & Fee Information
            </h2>
            
            {/* Fee Input Fields Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {/* Course Fee (Auto) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Course Fee <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={formData.total_course_fee}
                  onChange={(e) => {
                    setFormData(prev => ({ ...prev, total_course_fee: parseFloat(e.target.value) || 0 }));
                    setCourseFeeManuallyEdited(true);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-semibold"
                  placeholder="₹0"
                  data-testid="course-fee-input"
                />
                <p className="text-xs text-blue-600 mt-1">Auto-populated from fee settings, can be changed{courseFeeManuallyEdited ? ' (manually edited — auto-fetch disabled)' : ''}</p>
              </div>

              {/* Discount */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Discount (Optional) - Can add multiple
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.discount_id}
                    onChange={(e) => setFormData(prev => ({...prev, discount_id: e.target.value}))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">Select Discount to Add</option>
                    {discounts.filter(d => !appliedDiscounts.some(ad => ad.id === d.id)).map(discount => (
                      <option key={discount.id} value={discount.id}>
                        {discount.name} (₹{(discount.value || discount.amount || 0).toLocaleString('en-IN')})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      addDiscount(formData.discount_id);
                      setFormData(prev => ({...prev, discount_id: ''}));
                    }}
                    disabled={!formData.discount_id}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    + Add
                  </button>
                </div>
                {appliedDiscounts.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {appliedDiscounts.map(discount => (
                      <div key={discount.id} className="flex items-center justify-between bg-green-50 px-3 py-2 rounded border border-green-200">
                        <span className="text-sm">
                          {discount.name} <span className="font-semibold">- ₹{(discount.value || discount.amount || 0).toLocaleString('en-IN')}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeDiscount(discount.id)}
                          className="text-red-600 hover:text-red-800 font-bold"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <p className="text-xs font-semibold text-green-700 mt-2">
                      Total Discount: - ₹{formData.discount_amount.toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
              </div>

              {/* Scholarship */}
              <div className="col-span-1 md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Scholarship (Optional) - Can add multiple
                </label>
                <div className="flex gap-2">
                  <select
                    value={formData.scholarship_id}
                    onChange={(e) => setFormData(prev => ({...prev, scholarship_id: e.target.value}))}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  >
                    <option value="">Select Scholarship to Add</option>
                    {scholarships.filter(s => !appliedScholarships.some(as => as.id === s.id)).map(scholarship => (
                      <option key={scholarship.id} value={scholarship.id}>
                        {scholarship.name} (₹{(scholarship.value || scholarship.amount || 0).toLocaleString('en-IN')})
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      addScholarship(formData.scholarship_id);
                      setFormData(prev => ({...prev, scholarship_id: ''}));
                    }}
                    disabled={!formData.scholarship_id}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
                  >
                    + Add
                  </button>
                </div>
                {appliedScholarships.length > 0 && (
                  <div className="mt-2 space-y-1">
                    {appliedScholarships.map(scholarship => (
                      <div key={scholarship.id} className="flex items-center justify-between bg-blue-50 px-3 py-2 rounded border border-blue-200">
                        <span className="text-sm">
                          {scholarship.name} <span className="font-semibold">- ₹{(scholarship.value || scholarship.amount || 0).toLocaleString('en-IN')}</span>
                        </span>
                        <button
                          type="button"
                          onClick={() => removeScholarship(scholarship.id)}
                          className="text-red-600 hover:text-red-800 font-bold"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                    <p className="text-xs font-semibold text-blue-700 mt-2">
                      Total Scholarship: - ₹{formData.scholarship_amount.toLocaleString('en-IN')}
                    </p>
                  </div>
                )}
              </div>

              {/* Booking Fee (Auto) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Booking Fee {formData.seat_booking_amount > 0 && '(Auto)'}
                </label>
                <input
                  type="number"
                  value={formData.seat_booking_amount}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700 font-semibold"
                  placeholder="₹0"
                />
                {formData.seat_booking_amount > 0 && (
                  <p className="text-xs text-green-600 mt-1">✓ From booking</p>
                )}
              </div>

              {/* Admission Fee (Auto, Editable) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Admission Fee
                </label>
                <input
                  type="number"
                  value={formData.admission_amount}
                  onChange={(e) => {
                    const val = parseFloat(e.target.value) || 0;
                    setFormData({ ...formData, admission_amount: val, admission_fee: val });
                    setAdmissionAmountManuallyEdited(true);
                  }}
                  data-testid="admission-fee-input"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-700 font-semibold focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="₹0"
                />
                <p className="text-xs text-gray-400 mt-1">Auto-populated from fee settings, can be changed{admissionAmountManuallyEdited ? ' (manually edited — auto-fetch disabled)' : ''}</p>
              </div>

              {/* Admission Payment Breakdown */}
              <div className="col-span-2">
                <PaymentBreakdown
                  totalAmount={formData.admission_amount}
                  paymentModes={formData.admission_payment_modes}
                  onChange={(modes) => setFormData({ ...formData, admission_payment_modes: modes })}
                  label="Admission Payment Breakdown *"
                />
              </div>

              {/* Consultant */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consultant (Optional)
                </label>
                <select
                  name="consultant_id"
                  value={formData.consultant_id}
                  onChange={(e) => handleConsultantChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                >
                  <option value="">No Consultant</option>
                  {consultants.map(consultant => (
                    <option key={consultant.id} value={consultant.id}>
                      {consultant.name}
                    </option>
                  ))}
                </select>
                {bookingData && bookingData.consultant_id && (
                  <p className="text-xs text-green-600 mt-1">✓ From booking</p>
                )}
              </div>

              {/* Consultant Payout (Editable) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Consultant Payout
                </label>
                <input
                  type="number"
                  value={consultantPayout}
                  onChange={(e) => setConsultantPayout(parseFloat(e.target.value) || 0)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-orange-700 font-semibold"
                  placeholder="₹0.00"
                  min="0"
                  step="0.01"
                />
                <p className="text-xs text-gray-400 mt-1">Auto-populated from payout settings, can be changed</p>
              </div>
            </div>

            {/* Balance Fee - Prominent Display */}
            <div className="mt-6 p-6 bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-300 rounded-xl">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-700 mb-1">Balance Fee Payable</p>
                  <p className="text-xs text-gray-600">Course Fee - (Booking + Admission + Discount + Scholarship)</p>
                </div>
                <div className="text-right">
                  <p className="text-4xl font-bold text-green-700">
                    ₹{formData.balance_fee.toLocaleString('en-IN')}
                  </p>
                  <p className="text-xs text-gray-600 mt-1">
                    Receipt: {formData.admission_receipt_no || 'N/A'}
                  </p>
                </div>
              </div>
            </div>

            {/* Payout Note */}
            {selectedConsultant && consultantPayout > 0 && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  <strong>📌 Note:</strong> Consultant payout of ₹{consultantPayout.toLocaleString('en-IN', { minimumFractionDigits: 2 })} is already included in the Course Fee.
                </p>
              </div>
            )}
          </div>

          {/* Submit Buttons */}
          <div className="flex gap-3 justify-between pt-4 border-t">
            <button
              type="button"
              onClick={() => {
                // Debug logging
                console.log('=== GENERATING RECEIPT ===');
                console.log('formData.first_name:', formData.first_name);
                console.log('formData.admission_no:', formData.admission_no);
                console.log('formData.course_id:', formData.course_id);
                console.log('courses array length:', courses.length);
                
                const studentName = `${formData.first_name || ''} ${formData.middle_name || ''} ${formData.last_name || ''}`.trim();
                const courseName = courses.find(c => c.id === formData.course_id)?.name;
                
                console.log('Computed student_name:', studentName);
                console.log('Computed course_name:', courseName);
                
                // Validation
                if (!studentName) {
                  alert('Student name is missing. Please ensure all student information is filled.');
                  return;
                }
                if (!formData.admission_no) {
                  alert('Admission number is missing. Please save the student first.');
                  return;
                }
                if (!courseName) {
                  alert('Course information is missing. Please select a course.');
                  return;
                }
                
                const receiptData = {
                  receipt_no: formData.admission_receipt_no || formData.admission_no,
                  student_name: studentName,
                  admission_no: formData.admission_no,
                  admission_date: formData.admission_date,
                  course_name: courseName,
                  session: formData.session,
                  total_course_fee: formData.total_course_fee,
                  booking_amount: formData.seat_booking_amount,
                  booking_receipt_no: bookingData?.receipt_no || bookingData?.booking_id || 'N/A',
                  admission_fee: formData.admission_amount,
                  discount_amount: formData.discount_amount,
                  discount_description: discounts.find(d => d.id === formData.discount_id)?.name || 'Applied Discount',
                  scholarship_amount: formData.scholarship_amount,
                  scholarship_description: scholarships.find(s => s.id === formData.scholarship_id)?.name || 'Applied Scholarship',
                  balance_fee: formData.balance_fee
                };
                
                console.log('=== RECEIPT GENERATION BUTTON CLICKED ===');
                console.log('Final receiptData object:', receiptData);
                console.log('  - student_name:', receiptData.student_name);
                console.log('  - admission_no:', receiptData.admission_no);
                console.log('  - course_name:', receiptData.course_name);
                console.log('  - session:', receiptData.session);
                console.log('==========================================');
                
                // Call receipt generator directly with college info for proper tenant isolation
                generateAdmissionReceipt(receiptData, collegeInfo);
              }}
              className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center gap-2"
            >
              <DollarSign size={18} />
              Generate Admission Receipt
            </button>
            
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/students')}
              >
                Cancel
              </Button>
              {/* Hide submit button for approved students */}
              {!(editStudentId && isApproved) && (
                <Button
                  type="submit"
                  className="bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                >
                  {loading 
                    ? (editStudentId ? 'Updating...' : 'Admitting...') 
                    : (editStudentId ? 'Update Student' : 'Admit Student')}
                </Button>
              )}
              {editStudentId && isApproved && (
                <div className="text-yellow-600 font-medium flex items-center gap-2">
                  🔒 This form is read-only (Student Approved)
                </div>
              )}
            </div>
          </div>
        </div>
        </fieldset>
      </form>
    </div>
  );
};

export default StudentAdmission;
